<template>
  <div>
    <div class="title"> Pokemon Name Generator </div>
    <div class="date"> August 17th, 2020 </div>

    <div class="content">
        <div id="topic">
            <div>
                <img src="static/pokemon/mr_destructoid.png" width="40px">
            </div>
            <input id="input_text" type="text" style="margin: 10px 0px">
            <div> Here's what I'm thinking: </div>
            
            <div id="names">
            </div>
        </div>

        <div id="blog">
            Type any sequence of characters into the above model and see its generated Pokemon names! 

            <br/><br/>
            I recently started learning about sequence modeling. I gave myself the project of creating
            a Pokemon name generator using the knowledge I learned about the LSTM architecture. I was able
            to create and train this model in PyTorch. Honestly, the model works much better than I expected.
            I have saved all of the learned parameters in a readable form and reimplemented the forward propagation in JavaScript. 

            <br/><br/>
            PyTorch is so nice! It's so easy to define the forward propagation and have backpropagation implemented
            for you by default. Before learning about sequence modeling, I was learning about neural networks and I
            implemented one from scratch that learned on the MNIST dataset. The most difficult part of that
            was defining the backpropagation because it was my first time ever computing Jacobians and I kept
            mixing up indices when deriving the derivates for each parameter.
        </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'PokemonNameGen',
  mounted: () => {
      do_js();
  }
}

import {Tensor, Linear, sigmoid, tanh, softmax, random_idx} from "@/js/tensor.js";

function get_names(start_string) {
    if (start_string.toLowerCase() == "mrdestructoid")
        return ["...", "....", ".....", "......", "The time of man has come to an end."];
    let start = [];
    for (let i = 0; i < 5; i++) {
        start.push(word_to_tensor(start_string));
    }
    return tensor_to_word(lstm.sample(5, Tensor.cat(start, 1)));
}

function do_js() {
    let names_list = document.getElementById("names");
    let txt_input = document.getElementById("input_text");
    txt_input.addEventListener("input", () => {
        let names = get_names(txt_input.value);
        names_list.innerHTML = names.join("<br/>");
    });
    txt_input.value = "Ae";
    txt_input.dispatchEvent(new Event("input"));
}


class LSTM {
    constructor(n_x, n_y, n_a) {
        this.n_x = n_x;
        this.n_y = n_y;
        this.n_a = n_a;
        this.f = new Linear(n_a + n_x, n_a);
        this.i = new Linear(n_a + n_x, n_a);
        this.o = new Linear(n_a + n_x, n_a);
        this.c = new Linear(n_a + n_x, n_a);
        this.y = new Linear(n_a, n_y);
    }

    step(xt, a_prev, c_prev) {
        let concat = Tensor.cat([a_prev, xt], -1);
        let gamma_f = sigmoid( this.f.forward(concat) );
        let gamma_i = sigmoid( this.i.forward(concat) );
        let gamma_o = sigmoid( this.o.forward(concat) );
        let cand_c =  tanh( this.c.forward(concat) );

        let c_next = gamma_f.mul_(c_prev).add_(gamma_i.mul_(cand_c));
        let a_next = gamma_o.mul_( tanh(c_next) );
        let y_pred = softmax( this.y.forward(a_next), -1);
        return [a_next, c_prev, y_pred];
    }

    forward(X) {
        let T_x = X.shape[0];
        let m = X.shape[1];
        // let n_x = X.shape[2];

        let y_preds = [];
        let a_prev = Tensor.zeros([m, this.n_a]);
        let c_prev = Tensor.zeros([m, this.n_a]);

        for (let t = 0; t < T_x; t++) {
            let xt = X.index(t);
            
            let res = this.step(xt, a_prev, c_prev);
            a_prev = res[0];
            c_prev = res[1];
            y_preds.push(res[2]);
        }

        return Tensor.stack(y_preds);
    }

    sample(m, start) {
        let start_letters = start.shape[0];
        let y_preds = [];
        let a_prev = Tensor.zeros([m, this.n_a]);
        let c_prev = Tensor.zeros([m, this.n_a]);
        let xt = Tensor.zeros([m, this.n_x]);

        for (let t = 0; t < 20; t++) {
            let res = this.step(xt, a_prev, c_prev);
            a_prev = res[0];
            c_prev = res[1];
            let y_pred = res[2];

            if (t < start_letters) {
                y_pred = start.index(t);
            }
            xt.zero_();
            let c = Tensor.zeros([m, this.n_x]);
            for (let k = 0; k < m; k++) {
                let idx = random_idx(y_pred.index(k).arr);
                xt.arr[k][idx] = 1;
                c.arr[k][idx] = 1;
            }

            y_preds.push(c);
        }

        return Tensor.stack(y_preds);
    }
}


var alphabet = ['\n', '-', '2', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z']
var char_to_ix = {};
for (let i = 0; i < alphabet.length; i++) {
    char_to_ix[alphabet[i]] = i;
} 
var lstm = new LSTM(alphabet.length, alphabet.length, 15);
set_parameters();

function letter_to_vector(letter) {
    let x = Tensor.zeros([1, 1, alphabet.length]);
    let idx = (letter in char_to_ix) ? char_to_ix[letter] : Math.floor(Math.random() * char_to_ix.length);
    x.set([undefined, undefined, idx], 1);
    return x;   
}
function word_to_tensor(word) {
    if (word.length == 0) {
        return Tensor.zeros([1, 1, alphabet.length]);
    }
    let res = [];
    for (let l of word)
        res.push(letter_to_vector(l));
    return Tensor.cat(res, 0);
}
function argmax(list) {
    let idx = 0;
    let high = list[0];
    for (let i = 1; i < list.length; i++) {
        if (list[i] > high) {
            high = list[i];
            idx = i;
        }
    }
    return [idx, high];
}
// function words_to_tensor(words) {
//         let res = [];
//     for (let word of words)
//         res.push(word_to_tensor(word));
//     return Tensor.cat(res, 1);
// }
function tensor_to_word(T) {
    let [n_letters, num_words] = T.shape;
    let words = [];
    for (let m = 0; m < num_words; m++) {
        let word = "";
        for (let i = 0; i < n_letters; i++) {
            let [k, high] = argmax( T.index(i).index(m).arr );
            if (high == 0 || alphabet[k] == '\n') {
                break;
            }
            word += alphabet[k];
        }
        words.push(word);
    }
    return words;
}

function set_parameters() {
    lstm.f.weights.arr = [[ 5.2472e-02, -8.2033e-02,  8.3283e-02,  3.8478e-02, -5.9811e-02,
         -7.9548e-02,  5.3756e-02,  8.8440e-02,  6.4951e-02, -1.1906e-01,
          3.1851e-03, -1.1357e-02,  9.8728e-02,  1.1934e-01, -3.3348e-02],
        [ 2.1382e-02,  5.3390e-03,  2.1133e-02,  4.4974e-02,  5.6243e-02,
         -4.6232e-02, -4.8278e-03,  3.9902e-02, -7.9083e-02,  3.4670e-02,
          4.2111e-02,  6.4105e-02, -1.0716e-01, -5.4471e-03,  1.0230e-01],
        [-3.3033e-02,  1.1038e-01,  9.8683e-03,  8.9856e-02,  8.8708e-02,
          1.1525e-01, -5.9583e-02,  2.1846e-03, -4.0828e-02, -2.3595e-02,
         -1.5004e-02, -4.5865e-02,  4.8837e-02, -4.5169e-02,  2.3490e-03],
        [-3.3262e-02,  2.5713e-02,  8.3260e-03, -3.7164e-02, -2.7817e-02,
          3.8655e-03, -3.7307e-02, -2.3605e-02, -1.0355e-02,  3.2043e-02,
          1.1489e-01, -9.1024e-02, -1.0951e-01, -6.8957e-02,  2.8591e-02],
        [-7.4168e-02,  5.3110e-02,  4.2991e-02, -9.3492e-02, -3.3832e-02,
          4.9275e-02, -7.2177e-02, -7.2618e-02, -5.6568e-03, -8.6053e-02,
          5.5367e-02,  6.9924e-02, -7.3464e-02, -6.8012e-02,  8.7144e-02],
        [-5.0047e-02, -4.6997e-02,  1.3405e-02, -6.3756e-02,  3.2529e-02,
         -1.0276e-01,  2.6435e-02,  4.1006e-02,  3.5033e-02,  4.3966e-03,
         -6.6076e-02, -6.2171e-02, -5.1051e-02, -1.1116e-01,  2.9025e-02],
        [-2.4081e-02,  5.3147e-02, -5.5843e-02,  3.8870e-02, -5.8652e-02,
          8.2805e-02, -3.8162e-02, -3.2088e-02,  6.5165e-02,  4.4081e-02,
         -6.4151e-02, -9.2840e-02, -2.0806e-02,  9.0330e-03,  6.6875e-02],
        [-9.6256e-02,  3.1285e-02,  2.7072e-02,  1.1412e-01, -9.7806e-02,
          9.0366e-02,  4.8491e-02, -5.9412e-02,  4.6374e-02,  4.6224e-02,
         -4.5268e-02,  8.0269e-02,  4.6992e-02, -6.0916e-02, -9.5619e-02],
        [-4.8613e-03, -2.0465e-02,  1.1838e-01, -1.1098e-01,  9.0520e-02,
          6.1612e-02,  8.1276e-02,  7.5352e-02,  1.1016e-01,  1.1595e-01,
          4.2933e-02, -3.9677e-02,  1.1054e-01, -1.1669e-01, -3.2949e-02],
        [-1.0925e-01,  1.1442e-01, -6.5399e-02, -5.4541e-02, -9.3242e-02,
         -4.3144e-02, -1.0615e-01,  3.9255e-02, -5.8961e-02,  7.2253e-02,
          4.0739e-02,  5.2007e-02,  4.2621e-02,  6.9284e-03,  2.4105e-03],
        [-4.2649e-02,  4.7834e-03,  6.8804e-02,  8.1254e-02,  1.0172e-01,
          1.5430e-02,  1.0891e-01, -1.1253e-02, -5.2379e-02, -5.4614e-02,
         -5.1451e-03,  7.8559e-02,  2.9788e-02, -4.2987e-02,  4.9327e-02],
        [-9.7816e-02, -5.7379e-02, -8.8958e-02,  7.0919e-03,  8.9329e-02,
          9.3425e-02, -7.1342e-02,  1.1884e-01, -2.8276e-02,  4.2143e-02,
          4.2705e-02,  4.4061e-02, -8.8436e-02, -1.0982e-01, -1.4755e-02],
        [ 1.1265e-01,  5.4787e-02, -7.3260e-02,  2.4232e-03,  3.8109e-02,
         -8.8959e-02, -1.1395e-01,  2.2005e-02,  5.5265e-02, -2.6164e-02,
          3.4834e-02, -8.1982e-02, -8.2374e-02, -7.4698e-02, -1.0778e-01],
        [-1.1338e-01, -1.0239e-02, -6.3585e-03,  9.8383e-02, -1.0900e-01,
         -7.8582e-02, -2.4050e-02, -1.1766e-01,  6.4679e-02, -2.7946e-02,
          1.1637e-01, -1.1718e-01, -3.7979e-02,  7.7890e-03, -9.4038e-02],
        [ 6.9047e-02, -1.0146e-01, -3.7490e-03, -5.6736e-03,  6.0871e-02,
          1.8565e-02, -3.1032e-02, -2.5739e-02,  9.1566e-02,  1.1108e-02,
          1.1380e-01, -5.1397e-02, -5.4413e-02, -9.9852e-02, -7.8260e-02],
        [ 1.0442e-01, -9.9936e-02, -8.0302e-02, -9.1675e-02,  8.1845e-02,
         -4.6600e-02, -6.9540e-02, -4.5957e-02, -3.8748e-03, -2.3415e-02,
          6.0259e-03, -6.7296e-02, -1.1322e-01, -8.5290e-02,  2.3529e-02],
        [-6.1957e-02,  5.7664e-02,  1.1105e-01,  3.2106e-02,  1.1348e-01,
         -3.7285e-02, -2.1416e-02,  2.4204e-02, -1.7057e-02, -9.6025e-02,
          1.1020e-01,  1.0640e-01,  8.3770e-02,  6.8016e-03, -5.5664e-02],
        [ 5.6264e-02,  8.4823e-03,  9.2260e-02,  6.0435e-03,  8.3121e-02,
          4.3636e-02, -1.1858e-02,  3.9067e-02,  4.1652e-02,  6.2784e-02,
         -6.0826e-02, -6.6276e-02, -8.4531e-02,  5.6693e-02, -8.0301e-02],
        [-4.9938e-02, -1.0374e-01,  4.5080e-02,  7.6468e-02, -1.8566e-02,
         -6.1710e-02,  6.0059e-02, -3.4299e-02, -3.2948e-02,  6.8785e-02,
         -1.0932e-01, -9.2965e-02, -1.0144e-01, -8.5409e-02, -5.1194e-02],
        [-4.7193e-02,  1.7663e-02, -1.0899e-01,  9.5363e-02,  9.8548e-02,
          8.2777e-02,  4.3610e-02,  7.5289e-02,  3.7329e-02, -8.1611e-02,
          3.8880e-02,  3.7253e-02, -5.0912e-02, -3.4768e-03, -1.4909e-02],
        [-5.1253e-02, -8.9490e-02,  5.9885e-02, -4.5327e-02,  6.4412e-03,
         -5.6812e-02,  1.2457e-02, -5.6309e-02,  5.4359e-02, -3.6572e-02,
          8.4609e-02, -5.4668e-02, -1.0579e-01, -7.0797e-02, -8.8107e-02],
        [ 9.9005e-02,  4.9082e-02, -6.1013e-02, -6.3676e-02,  9.1099e-02,
         -4.7026e-02,  4.5778e-03,  9.7764e-02, -6.5504e-02, -8.6161e-02,
         -6.2443e-02, -8.2479e-02, -9.0167e-02,  5.0859e-04, -1.4885e-03],
        [-8.9052e-02, -2.7842e-02,  4.4245e-02,  4.6115e-02,  5.1871e-03,
         -5.8795e-02, -2.4829e-02, -2.3072e-02, -6.9000e-02,  4.2224e-02,
         -1.7779e-02, -1.0118e-01,  6.3170e-02, -1.8162e-02,  5.1286e-02],
        [ 4.6052e-02,  4.0912e-02,  6.1136e-02, -8.4870e-02, -5.3352e-02,
          5.2757e-02,  7.8413e-02,  8.7698e-05,  1.3759e-02, -5.4752e-02,
          5.4810e-02, -7.3313e-02,  4.5421e-02, -8.8550e-02,  1.0779e-02],
        [ 7.7113e-02,  1.0462e-01,  9.7315e-02, -7.9205e-02, -2.8346e-02,
          1.7001e-02, -2.6040e-02,  1.0877e-02,  5.7305e-02,  1.0695e-01,
         -6.6676e-02,  6.7996e-02,  1.0445e-01, -1.3420e-02, -1.0283e-01],
        [-9.8599e-02,  9.9016e-02, -9.6430e-02, -1.1135e-01, -7.6167e-02,
          1.7083e-02,  3.0696e-02,  2.0883e-02, -1.5400e-02, -2.2823e-02,
         -1.1046e-01,  9.1850e-03,  9.7585e-02, -6.9650e-02,  1.0221e-01],
        [ 1.1719e-01, -5.1958e-02,  5.6763e-02,  1.0112e-01, -6.5767e-03,
         -8.4853e-02,  1.1334e-01, -7.1931e-02,  3.6989e-02,  9.3397e-02,
          3.2437e-02,  7.3454e-02,  8.5855e-02,  5.1559e-02,  1.1124e-01],
        [-1.1584e-02, -7.2027e-03,  2.5642e-02, -4.5252e-02,  9.8603e-02,
         -4.8984e-02, -6.5884e-02, -1.4959e-03,  1.1459e-01, -8.4701e-02,
         -5.1824e-02,  7.4260e-02,  2.7528e-02,  6.9965e-02, -1.0030e-01],
        [-5.1447e-03,  9.6562e-02,  2.6542e-02, -5.9360e-02,  6.8951e-02,
          1.4531e-02, -2.2090e-02,  8.3865e-02,  3.7670e-02, -5.6129e-02,
         -8.8594e-02,  8.0576e-02, -9.2443e-02, -1.0183e-01, -2.0235e-02],
        [ 9.2440e-02,  3.2356e-04,  5.5666e-02,  3.5904e-02, -1.7667e-02,
          1.0229e-01,  1.8439e-02, -6.4452e-02,  1.1197e-01,  5.1101e-02,
          1.0020e-01, -4.0176e-02,  6.1836e-02,  8.2520e-02, -1.0473e-01],
        [-6.7276e-02, -3.6133e-02,  5.5238e-02, -6.2688e-02,  2.4085e-02,
          9.1557e-02,  9.9816e-02,  1.1382e-01,  5.7394e-03,  6.9988e-03,
         -9.5992e-02,  4.2353e-02, -1.7132e-02,  1.7827e-02, -1.2453e-02],
        [-7.4453e-02, -5.8972e-02, -1.0637e-01,  5.7259e-02,  6.0160e-03,
          3.0281e-02, -1.0984e-01, -1.0604e-01, -2.3893e-02, -6.5315e-02,
          2.2012e-02,  1.1767e-01,  1.0604e-01, -5.5265e-02,  3.0525e-02],
        [-1.5838e-02, -2.8786e-02,  1.1856e-02, -7.4356e-02, -5.9398e-02,
         -1.0612e-01, -1.6334e-04,  3.5632e-02,  7.0653e-02, -5.3463e-02,
         -2.1132e-02, -1.8462e-02,  1.0667e-01, -9.6577e-02,  2.8743e-02],
        [-5.6544e-02, -8.9672e-02, -1.4963e-02, -9.7787e-02, -3.6250e-02,
          3.1256e-02, -7.9293e-02,  8.7909e-02,  1.1683e-01,  3.9125e-02,
         -1.7725e-02, -6.1736e-02, -1.0000e-01, -1.9002e-02, -5.4569e-02],
        [ 5.1032e-02,  1.1906e-01,  9.2874e-02, -1.0557e-01,  8.0803e-03,
          6.8560e-02,  5.3073e-03, -9.8987e-02, -1.1841e-01, -5.8493e-02,
         -2.3939e-02, -4.1982e-03,  7.9037e-02,  6.4282e-02,  6.2321e-02],
        [-6.4180e-02,  9.2092e-02, -5.3374e-02,  9.5277e-02,  9.8672e-03,
         -6.9749e-02, -3.0808e-02,  5.5917e-02, -5.5411e-02, -4.4699e-02,
         -5.0165e-03,  4.0855e-02,  6.6798e-02, -3.5311e-03,  4.7299e-02],
        [-1.1586e-01,  9.4860e-02, -4.4920e-02, -1.0939e-01, -6.3085e-02,
         -3.1075e-02, -1.0996e-01, -5.9350e-02,  8.4236e-02, -8.9338e-02,
          3.2437e-02,  8.4991e-02,  2.6782e-02,  9.0412e-02, -4.7509e-02],
        [ 7.4580e-02, -4.6198e-02, -5.2804e-02, -7.4636e-03,  6.9505e-02,
          1.0687e-01,  3.0799e-02,  3.4142e-02,  4.6267e-02, -8.3490e-02,
          8.8925e-02,  4.3570e-02,  5.6042e-02,  2.8380e-02,  9.7938e-02],
        [-9.4258e-02,  7.4857e-02, -7.9114e-02, -7.5225e-02, -6.2042e-02,
         -6.8959e-02, -6.9522e-02,  2.0398e-03, -3.5641e-02, -4.7987e-02,
         -1.0434e-01,  4.2726e-02, -1.0340e-01,  7.4887e-02,  7.6605e-02],
        [ 8.0785e-02, -4.0389e-02, -3.4147e-02,  1.1046e-01,  1.0737e-01,
         -4.9419e-02,  5.4122e-02, -1.0763e-01,  4.7951e-02, -7.9727e-02,
          8.0938e-03, -3.5634e-02,  1.0808e-01,  2.2904e-03,  4.4291e-02],
        [-9.3281e-03,  7.7028e-02, -2.7238e-02,  5.3646e-02, -4.1928e-02,
          7.6534e-02, -1.1215e-01,  7.2117e-02,  1.2685e-02,  7.5546e-02,
          1.0685e-01,  8.5720e-02, -8.6070e-02, -3.1575e-02, -1.1282e-01],
        [ 8.7732e-02, -9.7991e-02,  2.9417e-02,  4.6067e-02, -2.6251e-02,
         -5.2255e-02, -8.5496e-02, -9.4345e-02, -1.0520e-02, -5.6051e-02,
          9.8565e-02, -4.6366e-02, -1.1479e-01,  9.8418e-02, -1.1551e-01],
        [ 9.2409e-02, -1.6835e-02, -1.0546e-01,  4.8078e-02,  5.6751e-02,
          1.1671e-01, -1.0267e-01, -6.1901e-02, -8.0359e-02,  9.3702e-02,
          1.6855e-02, -1.4789e-03,  5.5492e-02,  4.7448e-02,  4.5601e-02],
        [ 1.9421e-02, -5.8290e-02, -4.9613e-02, -7.6918e-02, -7.7793e-02,
         -7.7037e-02, -5.9680e-02,  8.5503e-02,  7.0114e-02,  3.2275e-02,
          3.8774e-02,  7.4388e-02,  2.4373e-03,  1.6391e-02, -1.1850e-01],
        [-5.0800e-02, -8.8161e-02, -7.3701e-02, -8.3109e-02,  1.0685e-01,
          4.8426e-02, -4.9803e-02, -9.5662e-02, -2.6779e-02, -1.1173e-02,
         -1.0645e-01,  2.3497e-02, -4.3526e-02,  7.3298e-02,  7.7536e-02],
        [-1.0377e-02, -1.0553e-01, -5.9102e-02, -4.8744e-02,  4.5111e-02,
         -8.3950e-02, -6.6852e-02,  5.8274e-02,  1.0091e-01, -1.0023e-01,
         -2.2091e-02, -3.5064e-02,  6.4512e-02, -1.7669e-02, -6.8765e-02],
        [ 7.5312e-03,  6.2044e-02, -1.1295e-01,  1.0335e-01, -6.9726e-02,
         -7.9544e-02,  2.1090e-02,  8.5010e-02, -5.6619e-02,  9.7295e-02,
         -4.7935e-02,  5.4112e-02, -2.1970e-02, -6.6307e-02,  2.2236e-03],
        [ 9.0511e-02,  7.5802e-02,  2.3244e-02, -1.0048e-02,  5.0024e-02,
         -2.9314e-02, -6.1233e-02,  9.9679e-02,  1.0836e-01,  7.7974e-02,
         -6.8004e-02,  1.6023e-02,  1.0681e-01, -7.6334e-02,  1.4111e-02],
        [ 1.7488e-02,  7.6099e-02, -7.9328e-03,  1.0854e-01, -4.0900e-02,
          2.2181e-02,  5.8729e-03,  8.3057e-02, -1.0139e-01, -6.1103e-02,
         -7.8215e-02, -4.8386e-03, -4.7001e-02, -2.1228e-02,  2.5297e-02],
        [ 1.0097e-03,  1.0468e-01,  1.3401e-02,  1.1852e-01, -4.1899e-02,
          6.9275e-02,  1.0232e-01, -1.1148e-01,  8.6623e-02,  1.2282e-02,
         -5.4313e-02, -2.6993e-02, -3.7066e-02,  2.5635e-02,  5.7384e-02],
        [-1.0031e-01, -1.1640e-01, -1.3191e-02, -7.2415e-02,  3.6756e-02,
          1.0375e-01, -6.9155e-02, -4.9406e-02,  3.4442e-02,  1.1707e-01,
          9.4699e-02, -6.1016e-02,  1.1168e-01,  4.8178e-02, -6.6281e-02],
        [ 3.9682e-02, -3.0832e-02,  9.9033e-03,  3.1662e-02,  1.5040e-02,
         -1.0268e-02,  8.1633e-02,  6.2733e-02,  9.6140e-02,  1.4869e-02,
          1.0196e-01, -2.2005e-02, -5.5283e-02, -1.0572e-01,  9.9062e-03],
        [-1.8316e-02, -6.2205e-02,  7.0291e-03, -3.9115e-02, -2.8130e-02,
          3.7750e-02,  5.0584e-02,  1.1122e-01,  3.6971e-02,  2.8968e-02,
          6.4532e-02,  9.7143e-02,  5.9632e-03, -1.1301e-02, -4.0126e-02],
        [ 5.6845e-02,  5.9109e-02, -2.3800e-03,  9.8733e-02,  4.4934e-02,
          3.1686e-02,  1.1307e-01,  7.4752e-02,  1.2863e-02,  3.3737e-02,
         -8.4886e-02, -6.7370e-02,  8.7526e-02, -7.4461e-05, -3.1763e-03],
        [-3.2697e-02, -6.4907e-02,  9.4366e-02, -2.3630e-02, -3.8546e-02,
         -4.2318e-02, -9.1233e-02,  9.8689e-02,  9.2608e-02, -1.1568e-02,
         -9.0435e-02,  2.9444e-02, -3.3540e-02, -4.2771e-02,  9.4433e-02],
        [-2.4543e-02, -3.6747e-02,  6.7962e-02,  8.1829e-02, -1.0776e-01,
          2.2461e-02,  9.7196e-02, -1.1432e-01, -8.1900e-02,  1.1455e-01,
          1.1582e-01, -3.9191e-02,  1.0652e-01,  5.3617e-02, -1.1434e-01],
        [ 1.7820e-02, -8.4006e-02, -1.9013e-02,  6.1820e-02, -8.0596e-02,
          9.5402e-02, -1.0840e-01, -1.0185e-02, -9.7132e-02,  2.6980e-03,
          8.4726e-02, -6.4837e-02, -3.1255e-02,  4.4406e-03, -1.0538e-01],
        [-3.3996e-02, -8.4401e-03, -1.1040e-01, -6.4615e-02,  1.0959e-01,
          4.9414e-02, -3.9974e-02,  5.8780e-03, -1.0196e-01, -5.0014e-02,
          4.3816e-02,  5.6035e-03,  2.6821e-02, -1.7447e-02, -1.0941e-01],
        [-9.8904e-02, -8.4254e-02, -6.5777e-02,  5.3629e-02, -1.1925e-01,
          9.5020e-02, -6.8633e-02,  1.1816e-01, -3.6547e-02,  9.8875e-02,
          8.1943e-02, -7.2125e-02,  7.0719e-02,  8.9144e-02,  9.9044e-02],
        [ 3.8445e-02, -9.4383e-03,  4.7281e-02,  4.8645e-02,  4.4203e-02,
          7.1390e-02,  1.0628e-01, -1.0628e-01, -5.5243e-02,  5.9296e-02,
         -5.0719e-02,  6.1283e-02,  8.5555e-02,  2.4733e-02,  1.1799e-01],
        [-5.2705e-03,  8.0338e-02, -5.7318e-02,  1.2623e-02, -4.7637e-02,
         -4.1361e-02, -5.9165e-03,  8.0586e-02,  7.0966e-02,  8.2473e-02,
         -7.9596e-02,  2.8580e-02,  2.5527e-02,  6.1068e-02,  3.5686e-02],
        [-8.4286e-03, -8.7120e-02, -1.8943e-02, -7.5719e-02, -6.8445e-02,
         -7.8839e-02, -9.7194e-02,  1.0315e-01,  1.1202e-01,  1.4850e-02,
         -8.5995e-02, -9.6412e-02, -8.0938e-02,  1.1088e-02, -4.1724e-02],
        [ 3.1552e-02,  2.0236e-02,  9.7215e-02,  5.9785e-02, -2.3207e-02,
         -6.0760e-03,  3.7549e-02, -8.7936e-02,  1.3221e-02, -9.5309e-02,
          4.5272e-02, -6.0246e-02, -3.5683e-02,  6.0146e-02, -7.0060e-02],
        [-1.0334e-01, -4.2146e-02,  7.9942e-02, -1.0474e-01, -1.0956e-01,
         -2.6490e-02, -1.0997e-02, -6.8136e-02, -2.5616e-02,  2.4176e-02,
          2.4275e-02,  3.8224e-02, -5.0981e-02, -4.4607e-02,  6.7693e-02],
        [ 8.3905e-02,  3.7304e-02,  9.6326e-02,  4.9231e-02,  1.1859e-01,
          9.1222e-02, -1.1679e-01,  3.5120e-02, -9.9536e-02,  9.9467e-02,
          4.1928e-02,  6.5992e-02,  7.5719e-02,  3.0390e-03, -1.0485e-01],
        [ 2.1231e-02,  3.0372e-03, -8.9602e-02,  8.3312e-02, -3.6106e-02,
         -5.9363e-02, -7.4527e-02,  1.0624e-01, -4.5633e-02,  4.1288e-02,
         -7.3984e-02, -5.6518e-03, -5.5335e-03, -7.0975e-04, -8.5779e-02],
        [ 4.3720e-02,  5.2764e-02,  1.0585e-01, -4.9168e-02,  7.8560e-02,
          1.6198e-02, -8.2932e-02, -1.0200e-01, -2.1370e-02,  1.0418e-01,
         -3.1441e-02,  1.1312e-01,  2.4726e-02, -1.0252e-01, -3.2726e-02],
        [ 5.5335e-02,  7.4860e-02, -6.9321e-02, -1.0102e-01,  7.2593e-02,
          1.1773e-01,  6.9578e-02,  1.1083e-01, -1.8741e-02,  3.0141e-02,
         -2.7241e-03,  2.3751e-02,  8.1298e-02, -5.0421e-03,  9.6924e-02],
        [-9.4773e-02,  9.9170e-02,  1.6573e-02,  1.1546e-01,  8.5724e-02,
          1.3116e-02,  1.0496e-01, -1.1116e-01,  1.0466e-01, -1.2375e-03,
          5.9578e-02, -7.8251e-02, -4.8114e-02, -1.1551e-01, -1.0009e-01],
        [ 2.3438e-02,  5.6390e-03,  1.7329e-02,  9.6819e-02,  1.1126e-01,
         -3.0377e-02, -1.6615e-02, -3.9883e-02, -7.0983e-02,  6.3437e-02,
          2.8096e-02,  8.0748e-02,  5.0402e-02, -6.4621e-02,  4.5917e-02]];
    lstm.f.biases.arr = [[ 0.0411,  0.0037, -0.0140,  0.0672,  0.0667,  0.0908,  0.0686, -0.1154,
        -0.0196,  0.0314, -0.0453, -0.0510,  0.0591,  0.0387,  0.0397]];

    lstm.i.weights.arr = [[-1.0482e+00,  6.8856e-01,  9.4069e-01,  8.0758e-02,  6.2716e-01,
          1.5894e+00, -2.8387e-01, -1.7883e+00,  1.5914e+00,  8.8159e-01,
          9.3143e-01,  2.4716e+00,  2.7198e-01, -2.6651e-01,  1.1650e-01],
        [-1.2018e+00, -8.2975e-01,  6.8828e-01, -3.1712e+00, -8.9759e-01,
          2.0870e+00, -2.2460e+00,  1.2431e+00,  2.4444e+00, -2.8261e+00,
         -6.3589e-01, -1.2733e+00,  7.5961e-01, -1.4008e-01,  1.4451e+00],
        [ 9.8654e-01, -2.3707e+00, -3.9768e-01,  6.0798e-01, -2.3453e+00,
         -1.8593e+00,  4.3955e-01, -1.8619e+00, -1.5701e+00,  8.6979e-01,
         -5.2811e-01,  1.0164e+00, -9.2633e-01, -1.0015e+00,  1.7135e+00],
        [-1.4125e-01, -2.3917e+00, -6.4962e-01,  9.0162e-01, -1.8604e-01,
          7.2643e-01,  1.0830e-02, -4.0474e+00,  2.7547e+00, -2.0835e-01,
          1.2037e+00, -1.0916e+00, -2.0755e+00,  4.5893e-01, -2.4633e+00],
        [ 2.7130e-01,  1.3379e+00, -4.1903e-01, -1.5841e+00, -5.1248e+00,
          4.8986e-01, -4.2741e+00, -2.3599e+00,  3.2981e-01, -5.0682e-01,
          3.9994e-01, -7.9387e-01,  9.8365e-01, -1.7729e+00, -2.2755e+00],
        [ 8.0895e-01,  3.0740e-01,  4.1630e-01, -1.2247e+00, -8.1555e-01,
         -3.0608e+00, -1.2463e+00,  9.4872e-01,  8.0247e-01,  1.0655e+00,
          1.0746e-01,  2.3442e+00,  1.5399e+00, -5.3666e-02,  3.2351e+00],
        [ 7.8226e-01, -1.3477e-01,  9.9660e-01,  3.7534e+00,  3.6316e+00,
         -7.7361e-01,  2.2983e+00,  1.8352e+00,  1.2670e+00, -9.4174e-01,
         -2.2626e-01,  1.4573e+00,  8.9436e-01, -1.6216e+00, -2.0118e-01],
        [ 5.8900e-01, -2.5048e-02,  5.8684e-01,  6.4964e-01, -9.7563e-01,
         -1.4883e+00, -3.2833e+00, -9.8006e-01,  4.1370e+00,  1.0680e+00,
         -1.5268e+00, -9.6431e-01, -4.0436e-01,  1.4073e+00,  2.0592e+00],
        [ 3.6712e+00,  5.9114e-01, -9.0143e-01,  6.2109e-01, -1.3448e+00,
          6.5286e-01, -1.2421e+00,  8.1546e-01, -2.6862e-02,  2.0438e-01,
         -1.5583e+00, -8.2440e-01, -1.2762e+00, -3.0665e-01, -4.3228e-01],
        [-2.4844e+00, -1.5052e+00,  4.3929e-01,  1.1706e+00,  3.7538e-01,
         -8.6287e-01, -1.9012e+00,  1.2688e+00, -1.3531e+00, -1.1285e+00,
          1.0889e+00, -6.7467e-02, -1.2071e+00, -3.3429e+00,  4.5096e-01],
        [ 2.0365e-01, -5.6905e-01, -1.1425e-01,  3.3421e+00, -5.0319e+00,
         -1.5367e+00,  1.0513e+00,  3.4549e-01,  3.9254e+00,  1.9921e+00,
         -2.0693e+00,  3.1825e-02,  2.5334e-01,  9.5698e-01, -1.5727e+00],
        [-2.0887e+00, -1.8444e+00, -9.7881e-01, -3.4578e+00,  4.6760e-04,
         -2.5433e-01,  3.0116e+00, -1.0807e+00, -1.5526e-02,  1.6135e+00,
         -6.3423e-01,  1.9092e+00,  1.2923e+00,  2.9915e-01, -1.0060e+00],
        [-1.5445e-01,  1.0460e+00,  6.4768e-01, -3.3443e+00,  2.3384e+00,
          3.2518e-01, -1.7245e+00, -7.6763e-01, -2.1836e-01,  1.9663e+00,
          5.8916e-02, -1.7261e+00, -1.6789e+00, -1.0601e+00,  2.2374e+00],
        [-1.0506e+00, -2.3001e+00, -2.7362e-01,  3.1325e+00, -3.9589e-01,
         -2.4129e+00,  1.6166e+00,  1.5117e+00, -8.7158e-01, -1.4074e+00,
         -3.8901e-01, -4.8025e-03,  1.8479e+00,  1.0216e+00,  1.4686e+00],
        [-1.1092e+00,  6.3880e-01, -7.6670e-01,  1.1303e+00,  2.3728e+00,
         -5.0511e-01, -2.2310e+00, -2.8070e+00, -8.6319e-01, -3.6289e-01,
          1.4284e+00,  1.7926e-01,  7.4964e-01, -7.6921e-01, -2.4687e+00],
        [-1.0954e-01, -5.2051e-02, -1.1582e-01, -5.4924e-02, -4.9231e-02,
          3.5372e-02, -1.0725e-01,  1.1594e-01, -1.1740e-01,  2.1749e-02,
         -6.7812e-02, -3.8568e-02, -7.8483e-02,  8.2395e-03, -8.9553e-02],
        [ 1.2879e+00,  1.6010e+00,  9.8165e-01, -1.6336e+00, -5.7712e-01,
         -2.4659e-01,  1.7099e+00, -2.6075e-01, -1.4728e-01,  3.1551e+00,
         -5.1111e+00,  1.5452e+00,  3.2486e+00,  2.6642e+00, -1.6385e+00],
        [-2.1624e-01,  1.8238e-01, -3.0488e-02,  2.7625e-02,  1.0809e-01,
         -2.5868e-01, -8.1464e-02,  1.3833e-01,  2.8056e-01,  1.2120e-01,
          4.8895e-01,  5.4317e-02,  2.5284e-01,  1.2965e-01,  1.1718e+00],
        [ 6.2752e-01,  3.3080e-01,  4.9768e-01, -4.5879e-01, -1.1778e+00,
          8.6006e-01, -4.0362e-01, -1.3815e+00, -5.5853e-01,  1.7653e+00,
         -8.5237e-01, -3.5520e-01,  1.0781e+00, -2.0989e+00, -8.8002e-01],
        [-5.0917e-01, -3.7301e-01, -3.0233e-01,  7.3781e-01,  1.9839e+00,
         -3.9739e-01, -1.0710e+00,  1.0097e-01,  1.3604e-01,  5.5605e-01,
          6.6949e-01,  2.1067e-01, -8.1144e-01, -1.2990e+00,  3.0591e+00],
        [ 7.3438e-01,  9.5430e-02, -1.5164e+00,  1.4642e+00,  9.7438e-01,
         -7.1723e-01,  4.5170e-01, -1.2138e+00,  1.4419e+00, -5.4618e-01,
          1.8800e-01,  2.1053e+00,  1.4332e+00, -2.9263e+00, -2.7335e+00],
        [-6.8049e-01, -5.1276e-01,  6.4016e-01,  2.6795e+00,  3.1807e-01,
          5.8972e-01,  3.9434e-01,  1.3160e-01,  1.5659e-01, -3.4549e-01,
         -1.2668e+00, -2.3814e+00,  3.6186e-01, -3.0040e+00,  6.1825e-01],
        [ 1.1371e-01,  1.0677e-02, -1.7249e-01,  1.6564e+00, -3.1969e+00,
         -8.5115e-01,  8.0367e-02,  2.1720e+00, -1.1656e+00,  3.5379e+00,
         -1.0082e+00,  4.3853e+00, -2.0181e+00, -8.2479e-01,  1.4260e-01],
        [-1.4566e-01, -2.3657e-01, -5.9070e-01,  1.3897e+00,  2.9106e-02,
         -4.5513e-01, -6.2238e-01,  1.1139e+00,  7.1870e-01,  3.1750e-01,
          8.3728e-01, -3.3131e-01, -1.0641e+00,  7.6434e-01, -8.6024e-01],
        [ 2.9290e+00, -9.1957e-02, -3.7818e-01, -2.2183e+00,  3.3405e+00,
         -3.1790e-01, -3.8902e-01, -7.3328e-01, -6.2029e-01, -7.7951e-01,
          1.7527e+00, -4.8823e-01, -1.1408e+00,  5.8957e-01,  4.4712e-01],
        [-1.7186e+00,  4.7844e-01, -1.1917e-01, -8.6775e-01, -1.5598e+00,
         -9.8304e-01,  2.8450e-01, -1.7948e+00, -5.5155e-01, -5.6257e-01,
          1.0967e+00, -2.3974e+00,  1.5870e-01, -1.8736e+00, -2.6978e-03],
        [ 4.1847e-01, -7.2910e-01, -1.9312e-01,  1.6563e+00,  4.2328e-02,
         -4.4363e-01,  6.5080e-01, -1.6186e-01, -2.6730e-01,  1.8177e+00,
         -1.0297e-01, -1.6533e+00,  2.0321e+00,  6.1386e-01,  1.8353e+00],
        [-4.3135e-01,  4.2992e-01, -4.8079e-01,  9.7776e-02, -1.7613e+00,
         -3.2045e-01, -3.0199e-02,  2.7192e+00, -5.5050e-02,  6.1094e-01,
          8.3416e-01, -1.3117e+00,  1.8689e+00, -6.5196e-01, -6.6354e-01],
        [ 2.0884e-01,  5.5702e-02, -3.4814e-02, -9.0487e-01,  3.2010e-01,
         -2.7129e-01,  1.3225e+00, -6.1937e-01, -2.0311e-01,  5.0244e-02,
          9.2925e-01, -2.2684e+00, -1.8034e+00, -2.2647e+00,  1.3881e+00],
        [-6.0193e-01, -2.2002e-01, -3.4876e-01, -4.1253e-01,  1.6376e+00,
         -4.1913e-01, -8.2229e-01, -1.4912e+00, -3.4199e-01,  5.1525e-01,
         -2.2142e-01, -1.4537e+00, -2.0102e+00,  8.7512e-01,  2.4713e+00],
        [ 3.9428e+00, -5.0187e-01, -1.0901e-01, -4.3842e-01,  2.6737e-01,
          1.1626e+00, -1.7490e-01, -2.2092e+00, -4.0273e-01,  1.4558e-04,
          3.5580e-01, -1.9351e+00, -2.0985e+00, -2.0519e+00,  1.1780e+00],
        [-1.7046e+00, -1.7152e-01,  1.2140e+00, -4.0380e+00, -5.8490e-01,
          9.2802e-02,  1.4772e+00, -1.4536e+00,  1.3972e+00, -5.6063e-01,
         -7.6101e-01, -2.0492e+00, -1.5118e+00,  1.8766e+00, -2.2075e+00],
        [ 5.0038e-01, -2.2383e-01,  1.9760e-01,  2.2178e+00,  1.5917e+00,
          1.7691e-01,  2.1180e-01,  1.8640e+00, -2.8337e-01, -1.5979e-01,
         -1.1072e+00,  2.4852e+00, -2.5118e-01,  2.7842e-01, -1.1675e+00],
        [ 1.3036e-02, -1.1572e-01, -1.8195e-01, -3.2047e+00,  1.8904e+00,
         -4.9211e-01, -1.8455e+00,  6.1863e-01, -1.4501e-01, -2.2694e-01,
         -5.3497e-02, -1.7097e+00,  1.0852e+00,  1.2084e-01, -1.9882e+00],
        [-3.9757e-02, -1.1024e-01, -1.1416e-02,  2.7867e+00,  1.4582e+00,
         -1.6902e-01,  2.1556e+00,  2.1089e-01, -8.6024e-02,  4.6588e-01,
         -4.7494e-01,  1.1496e+00,  2.4171e+00, -5.2166e-01,  1.8913e+00],
        [-1.7706e+00, -1.5049e-01,  2.7587e-01, -1.4173e+00, -1.7757e+00,
         -1.0076e-01,  7.3288e-01, -1.8627e+00, -4.0886e-01, -4.8941e-01,
          1.0804e+00,  3.3239e+00,  2.0433e+00,  9.6533e-01, -5.9181e-01],
        [ 1.2574e+00, -8.8336e-01, -2.5941e-01,  2.4348e+00,  9.7576e-01,
         -7.1765e-01, -1.1948e-01, -2.3908e-01, -6.0672e-01, -9.3744e-01,
          4.1288e-01, -3.7695e-01,  1.3644e+00,  8.3197e-01,  4.8522e-01],
        [-6.5042e-01, -3.9022e-01, -5.8713e-02, -1.1675e+00, -2.0932e+00,
          1.2487e-02,  3.2314e+00,  4.9207e-02,  2.3572e-01, -8.0030e-01,
         -1.9685e-01, -2.4385e+00,  1.4956e+00,  9.0610e-01, -8.3174e-01],
        [-1.6859e+00, -4.4348e-01,  7.1874e-01,  1.1623e+00, -2.2642e+00,
          1.8435e-01,  1.7138e-01, -2.3326e-01, -2.5404e-02,  4.7319e-01,
          8.4899e-01,  7.4038e-01, -1.5154e+00,  1.3956e+00,  3.3895e-01],
        [-7.2629e-01, -1.5478e-02,  1.2457e-01,  2.9433e+00,  1.2299e-01,
          3.0243e-01,  1.6770e+00,  1.3169e+00, -1.4844e-01,  5.8314e-01,
          3.0221e-01, -8.6034e-01, -9.4779e-01,  2.5986e+00, -7.7307e-01],
        [ 1.4550e+00, -7.0157e-02,  8.6770e-01, -2.2617e+00,  2.2743e+00,
         -3.7304e-01, -3.6726e-01,  1.6590e+00, -3.3159e-01, -3.2341e-01,
         -2.8876e-01, -5.9189e-01,  1.9846e+00, -9.6119e-01,  9.2308e-01],
        [-1.6411e-02,  4.7732e-01,  5.2712e-01,  3.7575e-01, -7.7357e-01,
         -2.4451e-01, -1.5714e-01, -4.7938e-01, -9.1361e-02, -4.5719e-01,
          2.0528e+00, -5.2295e-01, -5.0967e-01,  9.0858e-01,  4.9032e-01],
        [ 1.7566e+00,  3.8891e-01, -3.4282e-01, -3.1471e+00,  1.8334e+00,
          2.3753e-01, -2.9825e-01,  1.1331e-02, -2.7759e-01,  2.1302e-01,
          1.2822e+00, -5.9932e-02, -7.4073e-01, -1.1456e+00,  2.7200e+00],
        [ 6.5313e-01,  2.3305e-01, -2.9199e-01,  1.4543e+00, -3.2957e+00,
         -3.9161e-01,  1.9605e-01,  2.0510e+00,  6.3635e-03,  1.8129e-01,
          2.0543e+00, -1.6037e+00, -1.4890e+00,  9.1162e-01,  1.6634e+00],
        [ 3.5007e+00, -2.8376e+00,  1.8422e+00,  1.8259e-01, -4.3957e+00,
          4.5109e-01, -7.1306e-01,  1.0587e+00,  9.3030e-01,  2.9182e+00,
          9.2068e-01,  1.7880e+00, -3.5605e-01, -9.7171e-01,  1.0422e+00],
        [ 2.5412e+00, -6.7825e-01, -1.7724e+00,  3.9884e-01,  1.4435e+00,
          1.0835e+00,  1.7013e+00, -5.6146e-01,  1.1501e+00,  1.0588e+00,
         -4.1400e-01, -4.1085e-01, -1.0741e-01, -2.4132e+00,  5.2098e-01],
        [-5.1796e-01, -5.3840e-01,  9.1159e-01,  2.6784e+00,  6.0947e-01,
          7.2284e-01, -2.5389e+00,  2.5268e+00, -7.6018e-01, -6.3581e-01,
          3.5435e-02,  2.5125e-01,  2.5579e-01,  6.0821e-01,  1.4037e+00],
        [-1.7270e+00,  9.2899e-02, -6.1566e-01, -6.1103e-01,  5.3863e+00,
          2.3164e+00,  1.5781e+00, -6.0313e-01, -2.3573e+00,  3.3334e+00,
          1.0945e+00,  6.7472e-01,  6.3936e-01,  3.4716e+00,  5.2631e-01],
        [-2.1873e+00, -1.1633e+00,  5.9012e-01,  1.2380e+00, -2.8028e+00,
         -1.1452e+00, -5.4335e-01,  6.8673e-01,  1.3760e+00,  3.4860e+00,
         -4.3459e-01,  1.7651e+00, -1.6264e-01, -2.2925e+00,  9.7861e-01],
        [-3.9044e-01, -3.8068e-01, -7.5918e-01,  4.6783e-01,  2.0139e+00,
          1.2171e+00, -1.3469e+00, -7.1894e-01,  1.2842e-01,  8.3890e-02,
          1.8344e+00,  4.6211e-01,  7.1334e-01,  8.3571e-01, -1.8154e+00],
        [-2.8176e+00,  3.0966e+00,  3.3293e+00,  7.2510e-01, -1.8130e+00,
         -9.2486e-01,  1.0206e+00, -9.4009e-01,  2.5417e-01,  1.4225e+00,
          9.8965e-01,  3.9436e-01,  3.9333e+00,  2.2294e-01,  1.8558e+00],
        [ 1.7813e+00, -1.7750e+00, -2.2027e+00,  3.7582e-01,  2.2381e+00,
          1.5066e+00,  2.1816e+00,  2.9857e+00,  3.3358e+00,  2.6405e+00,
         -2.3205e-01,  1.3309e+00, -2.5152e+00, -2.2164e+00, -3.1710e+00],
        [-1.2579e+00,  4.0433e-01,  1.3023e+00,  4.2128e-01, -2.7999e-01,
         -7.2767e-01,  7.8618e-01,  1.3663e+00, -5.4416e-01,  1.8547e+00,
         -3.7866e-01,  3.8274e+00,  3.5015e-01,  1.7432e+00, -2.2876e-01],
        [-2.5856e-01,  6.0233e-01, -2.0954e-01, -4.5438e+00,  1.8340e+00,
         -5.8114e-01,  2.4461e-01, -3.3927e-01, -2.0380e+00,  2.9072e+00,
          2.5940e+00,  5.0288e-01, -1.3144e+00,  3.2397e-01, -2.0028e+00],
        [ 1.7658e+00, -1.5661e+00,  3.0409e+00,  2.6598e+00,  1.7781e-01,
          1.6866e+00,  3.6305e+00,  1.4640e+00, -2.9550e+00,  7.3872e-01,
         -2.2128e-01,  7.0505e-01,  4.5603e-01, -1.7665e+00,  6.8451e-01],
        [ 4.3686e+00, -2.0177e-01, -6.4219e-01,  5.0226e+00, -6.4292e-01,
         -7.1554e-01, -8.9541e-01, -3.7886e-01,  1.4231e+00, -4.1519e-02,
         -1.1229e+00, -1.4621e-01, -8.3472e-01,  6.2254e-01,  6.4268e-01],
        [ 1.2300e+00,  2.0100e+00,  3.1635e-01, -2.5046e+00,  1.0691e+00,
          3.4211e+00, -1.2162e+00,  5.8785e-01,  2.7013e+00, -7.2654e-01,
          5.6844e-01,  3.3336e+00, -1.3485e+00,  4.6353e+00,  2.2339e+00],
        [ 9.5908e-01,  3.6535e+00, -7.7752e-01, -8.0126e-01,  2.9235e-02,
          5.4782e-01, -4.6919e-01, -1.6421e+00,  4.5910e+00, -2.0885e+00,
         -4.7319e-01,  2.1420e+00,  1.9882e-01,  1.2819e+00, -9.0100e-01],
        [-1.7900e+00, -2.3083e+00,  2.8684e+00,  2.4268e+00,  1.8348e+00,
         -4.1255e-01, -1.9672e-01,  8.1219e-01,  1.4251e+00,  9.8823e-01,
         -1.0843e+00,  3.3630e-01, -7.2615e-01,  4.7841e-01,  5.1984e-02],
        [-1.3295e+00,  1.4336e+00,  2.6559e+00, -2.3567e-01,  7.6986e-01,
          3.6040e+00,  4.1876e+00, -1.8306e-01,  2.7503e+00,  1.2803e+00,
          8.1656e-01, -7.4132e-01,  1.0350e+00, -9.4235e-01, -3.6668e-01],
        [ 1.3520e+00, -9.2962e-01,  8.5596e-01,  2.1938e+00, -4.7636e-01,
         -9.1637e-01,  3.3374e+00,  3.4823e-01,  1.6808e+00,  6.1812e-01,
         -1.6648e-01,  1.9950e+00,  2.0962e+00,  8.2496e-01,  3.6064e+00],
        [ 2.9757e+00,  4.9376e-01, -4.7484e-01,  1.4929e+00, -9.3824e-01,
          1.0836e+00, -1.4990e+00,  3.1856e+00, -1.7808e+00,  5.0557e+00,
         -1.2937e+00,  2.1434e+00, -1.7007e+00,  5.4065e-02, -1.0985e+00],
        [-1.5055e+00,  1.9503e+00, -1.1132e+00,  2.0132e+00, -1.6170e+00,
         -9.1293e-01, -3.9540e+00, -2.2926e+00,  1.1155e+00, -2.7883e-01,
         -4.4266e-01, -1.5041e-01,  3.1627e+00,  4.0961e+00,  1.1348e+00],
        [-1.2228e+00,  8.2031e-01,  2.4462e+00, -2.2036e+00,  6.2708e+00,
          3.0904e-01,  8.1089e-01,  2.2224e+00,  1.7237e+00,  1.4193e+00,
         -4.5904e-02,  2.9453e-01,  7.1762e-01,  2.4186e+00,  1.3183e+00],
        [-3.1438e-01, -2.2267e+00,  1.9589e+00,  3.7775e+00,  7.4144e-02,
         -1.8684e+00, -1.1759e+00,  1.5251e+00, -2.7082e+00, -4.9060e-01,
          2.3531e+00, -1.6147e+00, -4.8582e-01,  1.2192e-01, -1.2306e+00],
        [-3.6886e-01, -5.5271e-01,  9.9802e-01, -2.4853e+00,  5.5296e-01,
          3.0429e+00, -1.0814e+00,  1.0109e+00,  3.3799e-01, -1.7957e+00,
          3.6759e+00, -7.9724e-01,  3.3087e+00, -1.3047e+00, -2.4698e+00],
        [-2.1580e+00,  7.8650e-01,  2.0819e+00,  5.3882e-01,  1.2529e+00,
         -1.7318e+00, -3.3112e+00,  8.2445e-01, -3.6987e+00,  6.8850e-01,
          4.7077e-01,  4.0548e-01, -2.8417e+00,  4.2349e+00,  5.3100e-01],
        [ 3.1173e+00,  2.2056e-01, -4.3071e-01,  2.8200e+00,  8.2993e-01,
         -1.7790e+00,  5.6532e-01, -1.4253e+00, -1.5367e+00,  1.7783e+00,
         -6.6758e-01,  1.6597e+00,  2.0144e+00, -1.9731e+00,  2.4119e+00],
        [ 1.6733e-01,  2.0982e+00, -6.0251e-01,  3.0695e+00, -1.4163e+00,
         -3.1904e+00,  1.9427e+00, -6.6221e-01, -5.0462e-01,  7.9493e-01,
         -2.1016e-01,  4.2758e+00, -1.7852e+00,  3.4138e+00,  2.3014e+00],
        [ 1.7727e+00,  3.2853e+00,  1.4488e-01,  2.6632e-01, -5.8613e-01,
          2.9130e-01,  2.7114e+00, -1.8109e-01, -3.5867e-01,  1.6372e+00,
         -6.3574e-01, -6.5110e-01, -1.6002e+00,  1.4913e+00,  3.7994e+00]]; 
    lstm.i.biases.arr = [[ 0.0583, -0.1729, -0.4456, -0.1052,  2.8715,  4.4767,  2.9590, -0.1712,
        -0.5981,  0.0990,  1.2012, -1.2881,  0.7117,  1.5655, -1.0568]];

    lstm.o.weights.arr = [[ 1.7859e+00,  1.6115e+00, -1.6216e+00, -1.0998e+00, -4.8282e-01,
          1.1902e+00,  1.0877e+00,  4.5610e-01, -2.4000e+00,  1.2588e+00,
         -3.5315e-01, -9.0049e-01,  4.0547e-01, -4.8870e-01,  3.2123e-01],
        [ 2.1835e+00,  1.5950e+00, -3.9829e-01,  3.5251e+00,  5.6623e-01,
         -1.6540e+00,  8.4029e-01, -2.0401e+00, -3.3778e+00,  4.4509e-01,
          1.8228e+00,  2.1576e+00,  9.3352e-02, -1.1368e+00, -2.1259e+00],
        [-7.5940e-01, -1.5370e+00,  4.0627e-01,  3.0037e+00,  8.1184e-01,
          1.1616e+00,  1.0373e+00,  3.1548e+00, -4.3718e-02, -3.0671e-02,
          8.3586e-01,  3.2549e-01,  1.8085e+00,  2.2381e+00, -1.8428e-01],
        [-1.9161e+00, -6.2712e-01,  9.6710e-01, -1.1616e+00, -2.2785e+00,
          8.8193e-01, -2.9798e+00,  1.8720e+00, -1.4843e+00, -1.1745e+00,
         -6.1470e-01, -3.1896e+00,  7.9174e-01, -1.4655e-02,  7.7396e-04],
        [-1.9580e+00, -2.1199e+00, -5.4159e-01, -2.4374e+00,  1.2555e+00,
         -6.7220e-01, -1.0862e+00,  3.4028e+00, -1.4725e+00, -2.5740e+00,
         -1.6539e+00,  2.5066e-01, -2.3531e+00,  9.8657e-01, -1.9759e+00],
        [ 6.1351e-01,  1.8574e+00, -1.9524e+00, -7.2363e-01, -8.7682e-01,
          4.9715e-01,  2.4210e+00,  1.5648e+00, -1.6218e+00,  8.0230e-01,
          1.1585e+00,  4.7326e-01,  2.6538e+00,  1.0274e-01, -1.0697e+00],
        [-4.2790e-02,  8.6453e-01,  4.0908e-01, -1.7563e-02,  1.2198e+00,
         -8.7998e-01,  2.4454e+00,  8.0626e-01,  1.2057e+00,  1.2695e+00,
         -1.4358e+00, -2.7527e+00,  1.6913e-01,  1.0370e+00,  1.1819e+00],
        [-1.1994e+00, -1.4955e+00, -8.0255e-01,  6.1345e-01,  9.1171e-01,
          3.1258e-01,  1.4075e+00,  1.6163e+00, -8.8154e-01, -2.0608e+00,
          1.2445e+00,  1.5002e+00,  1.7196e+00, -3.7387e+00, -1.0377e+00],
        [-7.1698e-01,  1.0844e+00,  4.5239e-01, -2.3658e+00,  1.5653e+00,
         -9.9822e-02,  8.3068e-01, -3.8712e-02, -1.0709e-01, -2.0174e+00,
          2.4914e+00,  2.0231e+00,  2.2608e+00, -3.9615e+00,  1.9787e+00],
        [-1.9472e+00,  1.3095e+00, -2.0097e+00,  1.4850e+00, -2.3906e+00,
         -3.2545e-01, -1.4736e-01,  1.5583e+00,  2.8054e-01, -1.1427e+00,
         -1.2169e+00,  1.1610e+00,  1.3839e+00, -7.6308e-01, -1.1459e+00],
        [ 9.2363e-01, -1.3583e-03, -1.1521e+00, -3.3982e+00,  4.0764e-01,
          4.7006e-02, -6.8802e-01,  2.3308e+00, -3.0782e+00, -6.1554e-01,
          2.9188e-01,  1.9229e+00, -1.1539e-01,  1.0991e-01,  1.9478e-01],
        [ 1.5495e+00,  5.0668e+00,  1.0856e+00, -3.6187e+00, -1.0691e+00,
          8.1829e-01, -1.1033e+00, -4.0193e-01, -8.3419e-01, -1.7313e-01,
          1.3310e+00, -4.0669e+00, -1.7225e+00, -1.3681e+00,  1.9470e+00],
        [ 7.4521e-01, -4.2630e-01, -5.1530e-01,  8.0262e-01, -3.9842e-01,
         -1.7061e+00,  6.5595e-01,  1.8369e+00, -3.8844e-01, -1.9282e-01,
         -6.3099e-01, -8.0400e-01,  3.6167e+00,  4.5384e-01, -1.9167e+00],
        [-1.5835e-02,  1.3841e+00,  1.1330e+00, -1.5299e+00,  5.3059e-01,
          1.9669e+00,  1.1106e+00,  7.7815e-01,  6.4193e-02, -2.5035e-02,
          2.7354e-01, -2.3684e-01, -2.7773e+00, -2.5049e-01, -2.0614e+00],
        [ 9.7586e-01,  3.5118e-01,  1.5515e+00,  1.3571e+00,  1.6273e-01,
         -1.3938e+00,  1.6125e+00,  5.1985e-01, -1.2150e+00, -1.2942e+00,
         -4.2173e-01,  7.4184e-01,  2.3551e+00, -1.4878e-01, -1.0709e+00],
        [ 4.1977e-02, -1.1508e-01,  1.1633e-01,  2.5577e-02,  7.3622e-02,
         -1.1064e-01, -4.9465e-02,  1.2542e-02, -3.3044e-02, -4.1544e-02,
         -1.1279e-01, -1.9361e-02, -5.4683e-02, -3.8122e-02,  1.7062e-02],
        [ 3.5078e+00, -9.6750e-01,  3.2396e+00,  1.4648e+00,  4.4091e+00,
         -1.7747e+00,  1.5051e+00,  3.5568e-01, -4.1944e-02,  3.5006e+00,
          1.0840e-01,  2.1671e+00,  1.0046e+00,  1.0054e+00, -3.6257e-01],
        [-4.1277e-01,  1.0480e+00,  2.0815e-01, -4.2846e-01,  6.8892e-01,
         -1.0573e-01, -2.2762e-01,  2.2163e-02, -1.8304e-02,  3.6826e-01,
          3.1475e-01,  7.3994e-02,  2.0410e-01,  2.1575e-01, -7.9356e-02],
        [-1.8361e-01,  9.6451e-01,  1.0371e+00, -2.0611e-01, -5.7282e-01,
          1.1706e+00,  1.0290e+00, -7.0173e-01, -2.0435e+00,  3.4191e+00,
         -6.5226e-01, -5.5199e-03,  1.8625e+00, -2.8732e-01, -6.6883e-01],
        [-1.1326e-01, -7.0673e-01,  1.8497e+00,  3.6755e-01,  6.6956e-01,
          1.0396e-01, -1.0121e+00, -1.1288e-01,  1.4706e+00,  2.4768e+00,
          6.7903e-01, -4.8259e-01, -8.9615e-01, -7.3568e-02,  3.3339e+00],
        [-1.4071e+00, -2.9067e-01, -7.3596e-02, -1.0841e+00, -5.8145e-02,
         -4.2717e-01, -5.2420e-01, -7.1940e-01,  2.5623e+00, -2.9818e+00,
          5.5579e-01, -6.4375e-01,  6.0190e-01, -4.0612e-01, -2.6172e+00],
        [-1.6155e-01, -2.2314e-01,  2.3339e+00, -8.6121e-02, -5.4513e-01,
          2.7123e+00,  4.3599e-01,  2.2046e-01,  1.6584e+00,  7.9542e-01,
         -1.3821e+00,  1.7592e-02,  3.3629e-02, -3.6564e-01,  3.0394e-01],
        [-3.4943e-01,  1.1890e+00, -1.3926e+00,  1.5767e-01, -7.1855e-01,
         -2.9552e+00,  9.1239e-01,  1.2803e+00, -4.1554e-01,  5.2542e+00,
         -1.9841e+00,  4.5433e-01, -1.5393e+00, -4.4700e-01,  5.5976e-01],
        [ 1.2334e-01,  8.1377e-01, -8.3810e-01,  2.2420e-02, -1.1487e+00,
          9.4101e-01, -1.2050e-01,  3.3725e-01,  1.9193e+00,  1.7057e+00,
          8.3019e-01,  4.1985e-02, -2.0610e+00,  1.8261e-01, -1.1127e+00],
        [ 8.1824e-01,  3.0340e+00, -1.8089e+00, -5.2713e-01,  2.2006e+00,
         -7.8649e-01, -4.1239e-01, -2.2924e-01, -1.9515e-01,  5.8981e-02,
          2.1555e+00, -4.7037e-01, -1.5179e+00, -3.0569e-01,  5.1121e-02],
        [-7.7100e-01,  2.7189e+00,  1.8605e-01,  2.2201e-01, -4.9513e-01,
         -4.0322e+00,  8.3721e-01, -8.6345e-01,  1.1113e-01, -6.4428e-01,
          1.0580e+00,  1.0679e-01,  5.0087e-02, -2.1833e-01,  2.7504e-01],
        [-2.5713e-01, -1.0941e+00,  8.2783e-01, -1.4302e-01, -1.6701e-01,
         -7.9077e-01,  1.3921e+00, -2.1041e-01, -8.3107e-01,  3.1488e+00,
         -5.8574e-01, -9.5092e-02,  1.9348e+00, -3.0158e-01,  9.2831e-01],
        [ 1.6443e-03,  1.5498e+00,  1.1690e+00, -8.8769e-02, -2.8562e-01,
         -5.3506e-01, -5.7555e-01,  1.8739e+00, -6.6469e-01,  1.6915e+00,
          9.9910e-01, -2.4693e-02,  1.7329e+00, -4.7812e-01, -2.9581e-01],
        [-1.9334e-01,  6.1115e-01, -2.0039e+00, -9.8260e-02, -3.4549e-01,
         -1.2169e+00,  3.5678e+00, -8.1661e-01, -3.0411e+00,  1.9831e+00,
          1.1640e+00,  6.4771e-02, -1.5274e+00, -4.1599e-01,  2.5307e+00],
        [-3.6465e-01,  1.1432e-01,  3.9741e-02,  1.7930e-01,  6.0256e-01,
         -7.4533e-01, -3.6358e-01, -5.3250e-01, -1.4249e+00,  2.2667e+00,
         -2.2321e-01, -1.4086e-01, -1.9646e+00,  1.4557e-01,  3.2416e+00],
        [ 2.5292e+00, -8.1598e-01,  1.4421e-01,  1.8008e-01, -1.0681e+00,
          4.1563e+00, -1.8623e-02, -1.1980e+00, -4.2546e+00, -6.8312e-01,
          1.0126e+00,  1.0484e-01, -1.6061e+00, -6.9443e-01,  3.5087e-01],
        [-3.4295e-01, -2.7329e-01,  3.1799e+00, -3.9838e-01, -7.0924e-01,
          2.1424e+00,  3.4896e+00, -8.0952e-01,  3.2731e+00, -1.9362e+00,
         -4.0298e-01, -1.2105e-01, -7.5772e-01,  8.9193e-01, -3.0278e+00],
        [-2.7786e-01, -4.6688e-01,  8.6569e-01,  4.4822e-02,  4.1681e-01,
          6.5839e-01,  7.3198e-01,  9.5273e-01,  1.1059e+00,  3.3169e-01,
         -1.3345e+00,  1.4495e-01, -1.1825e+00, -2.8350e-01, -1.6516e+00],
        [-2.9793e-01, -1.2006e+00, -2.8938e+00, -4.5594e-01,  3.9785e-01,
         -9.9689e-01, -2.0530e+00, -1.6295e-01, -3.0729e+00,  5.8969e-01,
          3.7019e-02, -2.0573e-02,  4.1773e-01, -3.0485e-01, -2.5135e+00],
        [-1.5503e-01, -9.0291e-01, -1.7333e+00,  5.8500e-01,  2.8399e-01,
          7.9390e-01,  3.9442e+00,  9.4983e-02, -1.4452e+00,  1.6692e+00,
         -8.9807e-01,  1.1171e-01,  2.1459e+00, -2.9332e-01,  2.0790e+00],
        [-5.7556e-01,  1.0197e+00,  2.9147e-01,  2.4652e-02, -9.9649e-01,
          1.5722e+00,  2.2271e+00, -6.1757e-01, -3.4884e+00, -1.3246e+00,
          1.5639e+00,  2.9866e-01,  1.8220e+00,  4.4733e-01, -4.4719e-01],
        [-1.1186e+00, -8.5587e-01, -1.4398e+00, -2.4427e-01, -1.4626e-02,
         -1.5301e+00,  5.7993e-01, -8.8310e-02, -2.0952e+00, -1.6243e-01,
         -1.8527e-01, -5.5802e-02,  4.0284e-01, -2.8700e-01,  1.1121e+00],
        [-2.5510e-01, -1.2864e+00, -2.2144e+00, -2.0151e-01, -9.2640e-01,
         -7.6621e-02,  5.8044e+00, -7.6383e-01,  3.2133e+00, -7.1471e-01,
          5.3738e-01, -1.7734e-01,  1.3723e+00,  5.8933e-01, -4.4407e-01],
        [-2.5299e-01, -1.5238e+00,  2.8030e+00, -1.9498e-01, -9.6986e-01,
         -2.1252e-01,  1.1186e+00, -1.4549e-01,  1.5372e+00,  1.4153e+00,
          1.3112e+00, -4.3236e-02, -9.0120e-01,  6.8111e-01,  2.3938e-01],
        [-3.9297e-01, -8.3018e-01, -5.4752e-01,  8.4779e-02, -2.7328e-01,
         -1.4557e+00,  3.4520e+00,  4.4193e-01, -1.6094e-01,  1.1358e+00,
          1.2073e-01,  2.1319e-02, -1.3917e+00,  1.6789e+00, -1.1188e+00],
        [-4.7198e-01, -1.0967e+00,  2.3771e+00, -4.2577e-01,  1.0994e+00,
          2.7905e-01,  3.6054e-01,  6.7390e-01, -2.6304e+00, -1.0978e+00,
         -1.0854e-01,  1.3952e-02,  1.4226e+00, -4.8280e-01,  6.5208e-01],
        [ 9.6257e-03,  1.7437e+00,  2.2474e+00,  9.2701e-03, -6.5019e-01,
         -1.3852e+00, -7.3236e-01, -2.8666e-01,  2.0344e-01, -7.8056e-01,
          2.5034e+00,  2.6343e-02, -3.8001e-01,  4.5137e-01,  1.4681e-01],
        [ 6.4337e-01,  1.3974e+00, -6.2755e-01, -2.6301e-01,  1.3714e+00,
         -9.3817e-01, -1.4584e+00,  1.3174e-02, -2.3852e+00,  1.2271e+00,
          1.5051e+00,  1.3447e-01, -8.4248e-01, -2.0052e-01,  2.3779e+00],
        [-2.4149e-03,  8.9779e-01, -2.1013e+00, -1.3936e-02, -1.0981e+00,
          2.5244e+00,  1.2959e+00,  1.2311e+00, -2.9182e-01,  1.1809e+00,
          2.0944e+00,  1.7074e-02, -1.3896e+00,  2.4845e-01,  1.5364e+00],
        [-6.7402e-03,  2.2766e+00, -1.4532e-01, -3.4619e+00,  1.5706e+00,
         -3.0568e-01,  4.9588e-01,  7.3419e-01, -8.7739e-01,  1.9526e+00,
         -1.1515e+00, -1.4253e+00,  3.5853e+00,  1.3424e+00, -1.1921e+00],
        [ 6.7098e-01, -1.8244e+00,  1.1411e+00, -3.5511e-01,  1.6964e-01,
         -2.5926e+00,  3.8532e+00, -2.0157e+00,  4.4184e-01, -2.2543e+00,
         -1.9715e+00,  5.0692e+00, -1.0066e+00, -1.2119e+00, -1.6633e+00],
        [ 1.3161e+00,  4.1588e+00,  2.6863e-01,  6.0922e-01,  1.5228e+00,
          7.4129e-02,  6.1648e-01,  3.2735e+00,  2.4544e-01,  1.2601e+00,
          5.5397e-01,  2.7491e+00,  4.2973e+00, -1.6018e+00,  5.8483e-01],
        [ 9.9102e-01,  8.7304e-01,  2.6949e+00,  1.5820e+00,  4.5245e-01,
          1.2559e-01,  2.7847e+00,  6.0858e-01,  2.2740e+00,  8.9039e-01,
         -2.6784e+00, -3.2640e-01,  1.2222e+00, -2.4175e+00, -4.4566e-01],
        [ 4.4431e-01,  2.0727e+00,  3.4882e-01,  8.7108e-01,  1.5124e+00,
          2.0539e-02,  1.6109e+00, -6.7188e-01,  1.9118e-02, -1.0960e+00,
         -3.0299e-02, -1.6808e+00,  1.0164e+00,  4.1612e+00,  2.0014e+00],
        [-2.4146e-01, -1.2810e+00,  1.0739e+00, -3.0507e-01, -7.8441e-01,
          1.7775e+00,  1.1128e+00,  5.3924e+00,  4.9458e+00,  4.3387e+00,
         -1.2058e+00, -9.7623e-02,  1.8882e+00,  1.7069e-01, -6.5195e-01],
        [-4.1898e-01, -1.6346e-01, -1.7170e+00,  2.7206e+00,  1.0373e+00,
          2.1640e+00, -1.0540e-01,  4.9530e-01, -6.1123e-01,  1.1653e+00,
         -8.1074e-01,  8.8835e-01,  1.4974e+00,  2.5731e+00, -1.1224e+00],
        [-5.8511e-02,  1.3118e+00,  4.6346e-01, -1.7720e+00, -7.5105e-01,
         -1.4745e+00, -1.2475e+00,  4.1686e+00, -4.4932e-01,  2.1177e+00,
          2.8705e-01,  1.2015e+00,  1.5018e+00,  9.7224e-01,  2.0776e+00],
        [-1.4251e+00, -3.9045e-01,  4.1457e-01,  3.5757e-01,  5.5195e-01,
          1.3399e+00,  4.9261e-01,  4.7278e+00,  5.7063e-01,  1.4029e+00,
         -7.0663e-01,  2.4926e-01, -1.0707e+00,  5.7288e+00, -1.0830e+00],
        [-1.6727e+00, -2.0686e+00, -1.1135e+00,  1.4282e+00,  6.9907e-01,
          1.3138e+00,  2.9321e+00, -1.2579e+00,  2.4679e+00, -6.6129e-01,
          7.1186e-01,  2.3162e+00, -1.9832e+00,  3.5491e+00, -8.2278e-01],
        [ 4.5016e-01,  2.8576e+00, -1.4510e+00, -1.3712e+00,  3.2566e-01,
          1.0934e+00, -7.2401e-01, -1.5143e+00,  3.9540e-01,  2.1025e+00,
         -3.2527e-01, -1.5994e+00, -7.9856e-01,  3.0762e+00, -2.7135e+00],
        [-2.7172e-01, -3.4978e-01,  1.3129e+00, -4.6974e-01,  1.0750e+00,
         -1.6443e+00,  4.7264e-01, -6.2397e-01, -1.6819e-02,  2.6482e+00,
          2.5365e+00,  9.9696e-01,  4.4698e+00, -1.1371e+00,  2.7148e+00],
        [-3.0108e-01,  5.6479e-03,  1.7294e+00,  3.9094e+00,  2.5602e+00,
          8.2977e-01,  2.9869e-03,  2.8852e+00, -1.3910e+00, -5.7477e-01,
          3.6447e+00, -1.8278e+00,  2.0147e-01, -5.5882e-01, -1.0021e+00],
        [ 3.8818e+00, -7.1474e-01, -6.1026e-02,  7.7044e-01,  5.1770e-01,
         -1.4535e+00, -1.4901e-02,  1.7946e+00, -1.2110e+00, -1.0911e+00,
          1.3293e+00,  5.4768e+00, -1.4831e+00,  4.4500e-01,  1.3367e+00],
        [ 2.1249e+00,  8.5241e-01, -5.0376e-02, -1.4747e+00,  1.3053e+00,
          8.6996e-02,  3.5355e-01,  1.5896e+00,  2.8561e-02,  1.8970e+00,
         -8.0331e-01,  8.7908e-01, -1.2765e+00,  2.1019e+00,  1.6185e+00],
        [ 2.0701e+00,  6.0861e-01, -3.5527e+00, -1.8670e+00, -6.9577e-01,
          1.7621e+00, -1.6164e+00,  8.0077e-01,  2.0895e-01,  1.3161e+00,
         -4.1294e-01, -3.1328e+00, -8.6905e-01,  4.7187e+00,  3.0047e-01],
        [ 8.0035e-01, -2.0119e+00,  8.4425e-01,  4.5272e+00,  7.6044e-01,
          6.3388e-01,  3.0660e+00,  3.5481e-01,  1.0352e+00,  9.3031e-01,
         -6.0411e-01,  5.2299e-01,  1.7587e+00,  5.9776e-01,  1.8739e+00],
        [ 5.7760e-01, -1.1730e+00,  1.9868e+00, -7.3972e-01,  2.6695e+00,
         -1.6003e+00,  1.0141e+00, -3.7015e+00, -1.7554e-01, -1.4795e+00,
         -1.9365e-01, -4.5751e-01,  6.6865e-01,  1.1603e+00,  2.7302e+00],
        [-4.8853e-01,  7.6943e-01,  2.2557e+00,  1.0611e+00,  1.0447e+00,
          3.1105e+00,  1.4392e+00, -3.1263e-02, -1.3327e+00, -3.2938e-01,
          3.3704e+00,  1.4712e+00, -6.4542e+00,  1.1773e+00, -2.6258e+00],
        [ 2.6813e+00, -1.4821e+00, -2.0480e+00,  4.1591e-01,  2.1153e-01,
         -1.2503e+00,  1.9746e+00, -2.9390e-01, -2.2947e+00,  4.5999e-01,
         -1.1599e-02, -1.2541e+00,  3.1873e-01, -2.3694e+00, -1.0295e+00],
        [ 7.9477e-01, -3.4982e+00,  1.9570e+00,  3.1433e+00,  2.2068e+00,
         -1.9324e-01,  1.8213e+00,  6.9100e-01,  4.1508e+00,  1.5994e+00,
         -1.3146e+00, -2.7745e-01,  2.7852e+00,  2.2358e+00, -1.8531e+00],
        [-2.3611e+00, -3.1736e+00,  1.5717e+00,  1.4704e+00,  1.8297e+00,
          7.2075e-01, -5.5901e-01, -1.8362e+00,  7.7545e-01,  1.0777e+00,
         -3.9796e+00,  2.6955e+00,  2.2401e+00,  6.8363e-01,  8.2797e-01],
        [-8.6516e-02,  1.6160e+00, -3.3297e-01, -1.1252e+00,  1.0939e+00,
         -4.6647e-01,  1.0852e+00, -2.5597e+00,  3.2408e-01,  2.3000e+00,
          3.2846e+00, -3.2673e+00, -3.3039e+00,  3.5394e-01, -1.2165e+00],
        [ 2.4204e+00,  2.9339e+00,  8.2461e-01,  1.3232e+00,  1.9431e+00,
         -2.0670e+00,  1.4506e+00, -6.1351e-01,  9.4065e-01, -1.2394e+00,
          2.0644e+00, -1.3446e+00, -2.3271e+00, -1.1668e+00, -6.6739e-01],
        [ 2.5538e+00,  5.1130e-01,  9.5575e-01, -7.9583e-01,  3.7378e+00,
          1.8319e+00,  8.7774e-01,  1.5185e+00,  8.0246e-01,  2.5006e+00,
         -2.3803e-01, -5.0155e-01, -5.2729e-01, -1.5137e+00, -3.1927e-01],
        [-1.3023e+00, -2.8785e+00, -4.3187e-01,  7.7074e-01, -1.0315e+00,
          1.9879e+00, -2.2581e+00, -1.3624e+00,  1.9955e+00, -1.0978e+00,
         -3.1259e-02,  1.5773e+00,  3.9291e+00,  2.1829e+00,  1.8074e+00]]; 
    lstm.o.biases.arr = [[ 0.3577,  2.1098,  2.5752,  2.4972, -0.9521,  2.6743, -1.4088, -0.4694,
         2.8912, -0.2945,  1.8978,  0.8955,  1.1585, -0.1082,  2.6098]];

    lstm.c.weights.arr = [[-2.4117e+00,  4.1747e+00, -2.6548e+00,  1.4891e+00, -4.8097e-01,
          5.0442e+00,  2.5343e+00, -1.8073e+00, -2.1934e-01, -4.1505e+00,
          2.3163e+00,  2.1014e+00, -2.2611e+00,  2.2260e+00, -1.2342e+00],
        [-3.1580e+00,  7.4375e-01,  6.4993e-01, -1.4911e+00,  1.6335e+00,
          3.3889e-01,  1.4438e+00, -1.0908e+00,  1.7138e+00, -1.0606e-01,
         -2.5053e+00,  5.7188e-01,  4.0650e-01, -1.8852e+00, -1.6535e+00],
        [-3.4954e+00, -8.3496e-02,  4.4295e+00,  6.9589e-02,  1.3338e+00,
          3.4345e+00, -4.3899e+00,  7.5966e-01,  1.3631e+00, -4.2245e+00,
          2.3444e+00,  3.3751e-01,  4.0044e-01,  1.5116e-01,  9.8669e-01],
        [ 1.0097e+00,  6.9237e-01,  3.2624e+00,  4.5480e-01,  2.0655e-01,
         -1.3603e+00, -1.4565e+00, -1.5807e-02, -9.0471e-03, -8.0351e-01,
          2.6794e+00, -4.2762e-01,  1.2014e-01,  1.0366e-01,  2.2051e+00],
        [-8.2632e-02,  8.4104e-01, -2.0876e+00,  1.5865e+00,  2.1115e+00,
          2.3656e+00,  2.8830e-01, -2.1400e+00, -2.2722e-01,  4.6624e+00,
         -2.0175e+00, -1.4416e+00, -1.7757e+00,  4.7981e+00, -3.3298e+00],
        [-1.3370e+00, -1.7748e+00, -2.0218e+00,  4.4603e+00,  6.9838e-01,
          3.3045e+00,  3.2538e+00, -5.9986e-01,  3.1235e+00, -4.3720e+00,
         -4.5285e-01,  4.4907e+00, -4.3835e+00,  5.7748e-01, -2.3331e+00],
        [-3.0656e+00, -4.0040e+00, -1.0735e+00, -1.3371e+00, -3.8083e+00,
          2.0658e+00,  2.5560e+00, -7.9468e-02,  3.5592e+00, -3.0995e+00,
         -3.9586e+00, -1.7794e+00, -3.4478e+00, -3.1824e+00,  1.8774e+00],
        [-7.1453e-01, -1.5856e+00, -5.8861e-01, -1.1039e+00, -2.2868e+00,
         -9.9217e-01,  6.1827e-02,  6.3619e-01, -5.5693e+00, -2.5299e-01,
         -1.1899e+00,  3.5008e+00, -3.7493e-01,  2.2188e+00, -1.6165e-01],
        [ 6.5086e-01,  1.1143e+00, -2.9024e+00,  3.7694e+00, -9.6522e-01,
          3.4366e-01,  3.3171e+00,  3.4454e+00,  7.6013e-01, -1.0352e+00,
          1.0332e+00, -4.6358e-01, -4.8513e-01,  2.3774e+00,  2.4917e+00],
        [-2.8182e+00, -2.0327e-01,  2.0468e+00,  3.5929e+00,  3.4184e+00,
          2.4985e+00, -2.2147e+00, -7.9907e-01, -2.9157e+00,  5.6573e+00,
          3.6224e+00, -4.0116e+00,  3.1498e+00,  1.4966e+00, -3.1725e+00],
        [-1.5768e+00, -2.0767e+00, -7.8668e-01, -1.8663e+00,  3.3903e+00,
          1.1898e-01, -5.5724e-01,  2.1554e+00, -2.7568e-01, -1.5954e+00,
         -2.0525e+00, -9.5429e-01, -1.7125e+00,  5.9326e-01, -6.0844e-01],
        [-1.0996e+00,  2.3729e+00,  1.3320e+00,  1.5639e+00, -2.2169e-01,
         -4.3345e-01,  2.8618e+00,  5.8708e+00, -4.0859e+00, -1.2949e+00,
          1.6194e+00, -4.5883e+00,  3.7027e+00, -2.8072e+00,  7.6862e-01],
        [-8.1078e-01,  2.1923e+00, -2.5363e+00,  1.5886e-02,  3.4801e+00,
          1.2991e+00,  3.1195e+00,  1.6532e+00,  1.6513e+00,  3.7491e+00,
         -5.9210e-01,  1.9948e+00,  1.5509e-02, -6.6427e-01,  8.1758e-01],
        [ 5.1040e+00,  3.0515e+00,  1.6508e+00,  9.2173e-01, -3.5577e-01,
         -1.8170e+00, -6.8400e-01,  1.6134e+00,  1.1125e+00, -4.8834e-01,
         -1.1765e+00,  1.7651e-01, -2.0762e-01, -1.3065e+00, -2.8173e-01],
        [ 7.1866e-01, -9.9193e-01,  2.2666e+00, -5.6258e+00, -2.1247e+00,
         -3.7513e+00,  7.6287e-01,  9.8625e-01, -3.8369e+00, -3.3774e+00,
          2.0133e+00, -1.6636e+00,  2.7586e+00, -2.5505e+00, -1.5976e+00],
        [-1.1185e-01,  1.0996e-01, -2.4515e-02, -1.3241e-02, -6.4370e-02,
          1.1719e-01,  9.0421e-02, -8.5501e-02,  7.1361e-02,  1.0812e-01,
          6.6520e-02,  5.6109e-02,  8.0221e-02, -1.1338e-01, -2.0756e-02],
        [ 1.9150e+00,  2.3209e+00,  8.3206e+00, -1.4249e+00,  2.0791e+00,
          5.2562e-01, -7.4000e-01, -2.4880e-01,  4.1448e-01, -2.2086e+00,
         -2.1928e+00, -2.4837e+00,  1.2940e+00, -5.4155e+00, -3.7077e+00],
        [-6.2636e-02, -8.2974e-01,  6.9493e-03, -5.1419e-02, -9.1063e-02,
         -1.2525e+00, -8.9685e-02,  2.7275e-01,  1.3565e+00,  9.1432e-02,
          7.7471e-01,  5.2827e-02, -1.4643e-01, -6.0080e-01,  1.8592e-01],
        [ 1.9421e+00,  8.9389e-01,  3.0793e+00,  1.1931e-01, -1.6860e-02,
          7.2513e-01, -2.3789e+00,  1.1681e+00,  1.0501e+00,  1.3526e+00,
          3.9541e+00,  2.3713e+00, -2.1118e+00,  8.9909e-01,  4.3059e-01],
        [-1.5329e+00,  2.8344e-01, -1.0317e+00, -2.5609e-01, -3.9860e-02,
         -7.3791e-01, -2.0702e+00, -2.0502e+00, -1.7160e+00,  5.0814e-01,
         -2.8622e-02,  2.7150e+00, -1.7602e+00,  2.0579e+00, -7.5950e-01],
        [-1.8056e+00,  6.0528e-01, -3.7366e-01,  9.1281e-02,  2.2842e-01,
         -2.9958e-01,  1.8393e+00, -1.7908e+00, -2.1227e+00,  4.5389e-01,
         -9.9526e-01,  2.0004e+00,  2.6387e+00,  1.9010e-01,  2.6081e-01],
        [ 1.7447e+00,  8.2427e-01, -1.9666e+00, -4.1255e-02, -1.4751e-01,
          1.1737e+00,  1.8873e+00,  1.5994e+00, -1.7361e+00,  4.8191e-01,
          3.2645e+00,  1.1050e+00,  2.2349e+00, -2.5099e-01,  2.2648e-01],
        [ 2.4113e+00, -2.7600e+00,  2.6248e+00,  4.7235e-02,  3.3233e-02,
          7.7240e-02,  2.0481e+00, -2.3493e+00, -1.9356e+00,  2.0384e+00,
          2.5177e+00, -2.7731e+00, -1.5797e+00, -1.0478e+00, -1.5273e-01],
        [ 2.0649e+00,  9.3742e-01, -3.7429e-01, -2.6455e-02,  7.0157e-02,
         -3.0725e-01, -1.7077e+00, -2.5610e+00, -1.6558e+00,  7.2361e-01,
         -4.1931e-01,  2.5942e+00, -1.4974e+00,  2.5298e+00,  1.5896e-01],
        [ 2.4686e+00,  1.7800e+00,  5.8493e-02,  1.0791e-02, -7.6936e-03,
         -4.2171e-01,  1.8534e+00, -2.0021e+00, -1.1671e+00,  9.7007e-01,
         -9.0401e-01,  2.2627e+00, -1.7508e+00,  2.4499e+00,  2.8242e-02],
        [-1.1054e+00,  1.1649e+00, -1.3935e+00,  7.7590e-03, -2.9626e-02,
         -1.0503e+00, -2.3508e+00, -4.9626e-01, -1.3191e+00, -9.9868e-02,
         -1.2989e+00,  7.3149e-01,  2.1958e+00,  1.1822e+00,  4.1732e-01],
        [-1.3698e+00,  1.1628e-01,  2.5173e+00,  1.9961e-02, -3.6741e-02,
         -1.9177e-02, -1.8442e+00, -1.6468e+00, -6.8481e-01,  1.2639e+00,
          3.6560e+00,  4.2967e-01, -2.6147e+00, -1.4784e+00, -1.6397e-01],
        [ 1.5732e+00,  9.6917e-01, -1.5137e+00,  7.1155e-02,  1.0859e-01,
         -8.6892e-02, -1.1620e+00,  2.3699e+00, -9.3848e-01,  4.5147e-01,
         -2.9808e-01,  1.0260e+00,  2.6510e+00, -7.6786e-01,  2.0689e-01],
        [ 1.9467e+00,  1.1394e+00,  2.0985e+00, -1.0141e-01,  1.4296e-01,
          8.0956e-02,  2.3211e+00,  1.6882e+00, -4.5717e-01,  1.0642e+00,
         -1.2928e+00,  8.7961e-01, -1.5942e+00,  9.1910e-01, -5.6214e-01],
        [-1.4539e+00,  7.6501e-01, -1.4805e+00, -1.0507e-01, -9.3224e-02,
          1.4478e-01, -1.7053e+00,  6.0136e-01,  2.8618e+00,  5.4364e-01,
         -3.6656e-03,  1.4388e+00,  1.4027e+00, -1.3982e+00, -6.2814e-01],
        [-2.7938e+00,  3.6680e-01, -1.7175e+00, -7.6063e-02,  6.2849e-02,
          1.3535e+00,  2.0547e+00,  1.0584e+00,  2.1082e-02,  9.2259e-01,
         -1.1229e+00,  1.5443e+00, -2.4662e+00, -3.4644e-01,  5.0402e-02],
        [-1.3661e+00,  9.3266e-01, -2.2802e+00,  2.6611e-01,  1.0365e-01,
          2.7474e-01, -2.9513e+00,  1.0843e+00,  3.7823e+00,  1.4240e-01,
         -2.7721e-01, -4.4228e-01,  2.0701e+00, -1.6620e+00,  4.5641e-01],
        [-1.8186e+00,  1.5448e-01,  2.9172e+00, -7.8626e-02,  6.3373e-02,
         -9.4391e-02,  1.4192e+00, -2.1866e+00, -1.5629e+00,  6.7887e-01,
          3.2929e+00, -1.6265e+00,  1.9922e+00, -1.4122e+00,  8.0690e-02],
        [ 2.2508e+00,  6.5164e-01, -5.6474e-01,  1.2988e-01,  9.1286e-04,
          3.5142e-02, -6.8753e-01,  1.2804e+00, -7.9242e-01,  6.4156e-01,
         -6.0478e-02,  1.9640e+00,  2.2890e+00, -1.4498e+00,  3.2149e-01],
        [ 1.9004e+00, -1.7399e-02, -7.7610e-01, -1.7345e-01, -1.0444e-01,
          9.8656e-02,  2.2893e+00,  1.1536e+00,  2.4338e+00,  3.5341e-01,
          3.5924e+00,  2.2210e+00,  2.6579e+00, -9.7706e-01, -2.7995e-01],
        [ 1.1217e+00, -2.9505e+00, -1.9877e+00,  1.4369e-02,  1.2734e-01,
          3.7445e-01, -2.2144e+00, -6.6052e-01,  1.1429e+00,  2.8836e-01,
         -6.8382e-01, -2.0365e+00,  2.7165e+00, -1.1151e+00,  1.0899e-01],
        [-1.9338e+00, -2.5306e+00,  2.9143e+00, -2.1249e-01,  2.6688e-01,
          4.9753e-01, -2.0377e+00,  2.3114e+00,  2.8679e+00,  3.7501e-03,
         -1.2544e-02, -2.5141e+00,  4.8949e+00,  2.4627e+00,  6.7343e-02],
        [ 2.1062e+00, -2.2472e+00, -8.8397e-01,  9.9802e-02,  1.5498e-01,
          2.1190e-01, -3.5181e+00,  1.7387e+00, -2.5205e+00,  6.2928e-01,
         -5.3824e-01,  7.3911e-01,  2.8095e+00, -1.2442e+00,  2.1824e-01],
        [ 6.2096e-01, -9.3998e-01,  2.9395e+00, -8.9925e-02,  8.6281e-02,
          2.7491e-01,  1.5694e+00,  8.5934e-01, -1.4136e+00,  4.9554e-01,
          4.0765e+00, -1.3702e+00, -6.4583e-01, -1.3988e+00,  5.4508e-02],
        [ 1.7341e+00, -2.0853e-01, -1.3884e+00,  2.7327e-01,  8.4705e-02,
         -1.6148e-01, -2.3741e+00,  1.7617e+00, -9.6075e-01,  5.2706e-01,
         -6.2655e-01, -1.3135e+00,  1.6083e+00, -1.8823e+00,  6.4697e-02],
        [-1.8812e+00, -2.2124e+00, -2.2441e+00, -3.5126e-02, -2.5141e-02,
          3.4409e-01, -1.8935e+00, -2.7067e+00,  2.1760e+00, -2.6361e-01,
         -9.5122e-02, -1.6137e+00,  2.8966e+00,  1.7952e+00, -3.4832e-01],
        [ 1.6418e+00, -2.8878e+00, -1.7246e+00,  5.8947e-02, -5.4248e-02,
         -1.9312e-01,  1.1349e+00,  8.1223e-01, -8.9319e-01, -3.1241e-01,
         -9.6408e-01,  1.3610e+00,  1.1497e+00, -1.3122e+00, -5.3754e-02],
        [-1.8542e+00,  4.8162e-01,  2.1296e+00, -5.6894e-03,  5.3977e-02,
          1.3493e-01,  1.1019e+00, -1.1885e+00,  1.1966e+00,  3.5770e-01,
         -6.5044e-01, -1.4895e+00, -1.6347e+00,  9.2418e-02, -6.7469e-01],
        [-1.5109e+00,  9.3133e-01, -5.2247e-01,  1.4643e-01,  6.0561e-02,
          2.5979e-01, -1.9771e+00, -2.3504e+00,  2.7222e+00,  5.5430e-01,
         -8.5096e-01,  1.1784e+00, -8.3605e-01,  2.3586e+00, -3.3889e-01],
        [-1.2376e+00, -5.5017e+00,  5.1067e+00,  1.7055e-01,  1.8521e+00,
          4.3751e+00,  4.8540e-01,  3.1143e+00, -3.0328e-01, -5.8701e-01,
          5.6418e+00,  5.6266e+00, -2.4584e+00, -6.2864e-01,  4.6608e-01],
        [ 4.0821e+00,  1.5478e+00, -1.1380e+00,  5.1680e-01, -1.8968e-01,
          4.7803e+00,  3.5296e+00, -1.2360e+00,  1.9905e+00,  1.6785e+00,
         -3.7573e-01,  2.3465e+00, -1.0141e+00, -4.0031e+00, -8.7256e-01],
        [ 3.2397e-01,  9.2569e-01,  2.7274e+00,  2.3125e-01,  1.3774e+00,
          4.7203e+00,  5.4832e+00,  5.4366e+00, -3.4798e+00, -7.8975e-02,
         -2.2718e+00, -6.1769e-01,  7.8419e+00,  1.9878e+00,  4.1561e+00],
        [ 1.5512e+00,  2.4824e+00, -2.8769e+00,  1.9568e+00,  2.0606e+00,
          3.1493e+00, -1.0370e+00,  1.5827e+00,  1.4429e+00, -3.7917e+00,
         -2.5719e+00, -2.3493e+00, -2.9159e+00, -3.1548e+00, -1.1623e+00],
        [-1.9982e+00, -3.9582e+00,  4.9476e+00,  1.0447e+00, -1.3121e-01,
          5.2895e+00,  3.6473e+00,  3.4045e-01,  8.8340e-01, -1.5064e+00,
         -8.5792e-01,  3.4221e-01, -4.8387e+00, -1.1079e+00,  1.3773e+00],
        [-1.5593e+00,  4.8064e+00,  3.0137e+00,  4.0116e-01, -6.2984e+00,
          3.2766e-01,  2.2860e+00,  1.2745e+00,  2.2651e+00,  5.1290e-01,
         -8.1682e-01, -6.0955e-01, -2.1545e+00,  6.7335e+00, -4.0976e+00],
        [ 3.0474e+00,  6.6311e-01, -2.8512e+00, -6.4391e+00,  2.8107e+00,
          2.3754e-02, -1.5017e+00, -6.8148e+00,  4.3698e+00,  7.1752e-01,
         -3.1010e+00,  1.0332e+00, -4.8824e+00, -2.2861e+00, -2.2197e+00],
        [-5.9229e+00, -9.2178e-01, -2.6160e+00, -1.3283e+00,  1.2715e+00,
          7.5632e-02,  1.6985e-01,  3.7222e+00, -2.4444e-01, -3.8748e+00,
         -1.9728e+00, -2.5522e+00, -1.9800e+00,  2.8614e+00, -1.9271e+00],
        [-4.9333e-01, -7.0685e+00,  5.3255e+00, -4.9638e-02, -1.5264e+00,
          5.7067e+00,  9.1106e-01, -5.5945e+00,  1.2405e+00, -4.6217e+00,
          4.4445e-01, -6.9020e+00, -6.8454e+00, -1.6458e+00, -3.8381e+00],
        [-1.3610e+00,  3.2290e+00, -1.7447e+00, -5.4070e-01,  2.9861e+00,
          3.8366e+00,  4.8278e-01,  5.8717e-01, -1.1125e+00, -9.1485e-01,
         -3.0705e+00, -3.6082e+00,  2.6402e+00, -5.6737e-01,  1.7496e+00],
        [ 3.5702e-01,  4.4140e-01, -4.0442e+00,  2.6575e+00,  9.6284e-01,
          9.7919e-01,  3.7094e+00,  3.7741e+00,  6.1443e+00, -5.3655e+00,
         -3.6546e+00,  1.2756e+00,  1.7278e+00,  1.5462e+00,  1.0079e-01],
        [-8.6718e-01, -1.8121e+00, -3.2612e+00,  2.0497e+00, -1.2502e+00,
          7.0954e-01,  1.2261e+00, -2.6359e+00,  3.5262e+00,  5.5816e-03,
         -1.6083e+00,  3.9309e-01, -8.0204e-01,  7.4405e+00, -2.9645e+00],
        [-4.8934e+00,  4.7214e+00, -3.6664e+00,  4.9806e-01, -2.4168e+00,
          2.1820e+00,  7.7758e-01, -9.0650e+00,  5.5874e+00, -2.6266e+00,
          9.0229e-01, -2.3095e+00,  1.3461e+00,  4.7732e-01,  2.3065e+00],
        [-2.0596e+00, -3.5168e-01, -4.1697e+00,  1.1092e+00, -3.2363e+00,
          2.0854e+00,  2.7150e+00, -3.6355e+00,  2.6323e+00, -5.3960e+00,
          7.1021e-01, -4.1741e+00, -4.0994e+00, -4.9072e-01, -6.3863e+00],
        [-4.4059e+00, -2.2329e+00,  7.4434e+00, -7.0862e-02,  9.0630e-01,
          4.5610e+00,  1.7244e+00, -5.2849e+00,  2.8857e+00, -3.9278e+00,
         -2.3744e+00,  3.6530e+00, -6.0664e+00, -2.6576e+00,  1.1987e+00],
        [ 3.0025e+00, -9.6082e-01, -2.0242e+00,  1.9521e+00, -1.3550e+00,
          4.1977e+00, -8.0726e-01,  1.1904e+00,  1.7937e+00, -5.1715e+00,
         -2.0562e+00,  3.3551e+00,  2.0089e+00,  3.2449e+00,  2.8525e+00],
        [-1.0954e+00,  1.7142e+00, -2.5826e+00, -2.1244e+00,  4.7626e+00,
          1.2628e+00,  8.4119e-01,  4.4570e+00,  1.8808e+00, -4.1475e-01,
         -7.3334e-01,  2.9745e+00,  8.3811e+00, -3.8326e+00, -4.7512e+00],
        [-1.8585e+00,  2.0690e+00, -5.4314e-01,  2.6211e+00,  1.3740e+00,
         -5.9165e-01,  2.1010e+00, -3.2510e+00,  4.9369e+00, -2.2991e+00,
         -1.4787e+00, -6.6486e+00, -2.0312e-01, -4.6596e+00, -6.9273e-01],
        [ 6.7845e-01, -2.0560e+00, -7.5181e-01, -6.2617e+00,  3.8053e+00,
          3.3713e+00, -1.1374e+00,  5.2525e-01,  3.0008e+00, -2.1992e+00,
          1.4906e+00, -6.0928e+00, -4.0635e-01,  3.5231e+00,  3.2268e+00],
        [ 6.0169e-01, -2.6519e+00,  8.6837e-01, -9.9108e-01,  2.5639e+00,
          5.2490e-01,  1.9658e+00,  2.7248e+00,  3.3857e+00, -3.4876e+00,
         -3.8463e+00, -2.2985e+00,  2.0685e+00,  1.7167e+00,  4.1328e-01],
        [ 4.6356e+00, -4.1859e-01,  7.8645e+00, -3.9224e-01, -2.1941e+00,
          1.5815e+00,  3.2535e+00,  1.1566e+00,  1.4381e+00, -1.0667e+00,
          4.5920e+00,  3.3326e+00, -5.7998e+00, -2.7713e+00,  1.2592e-01],
        [ 1.7308e+00,  4.4748e+00, -2.5857e+00,  9.4871e-01, -3.4184e+00,
          2.9074e-01,  2.0462e+00,  5.5839e+00,  1.4338e+00,  3.5380e+00,
         -2.2322e+00, -4.0247e-01, -2.0710e+00, -2.1097e+00, -1.2239e+00],
        [-1.1708e+00, -3.7489e-01, -1.4251e-01,  3.1065e+00,  6.0805e-01,
         -4.6254e-01,  6.9124e-01,  2.9062e+00,  3.6903e+00, -2.4733e+00,
         -1.4920e+00, -1.7276e+00,  1.8844e-01,  2.0286e+00,  3.3117e+00],
        [ 5.7934e+00,  3.8697e-01, -4.3125e+00, -3.3343e-01, -3.4725e+00,
         -1.5143e+00,  2.2501e+00,  3.3508e+00,  9.5363e-01, -2.1360e-01,
         -2.8239e-01,  1.2918e+00, -3.6484e-02, -1.3100e+00,  2.5212e+00],
        [ 2.6414e+00, -6.5317e+00, -1.1163e+00,  9.5209e-01, -3.2985e+00,
          1.9684e+00, -3.6878e-01, -3.8039e+00, -1.7259e+00, -4.8689e+00,
          6.0728e+00,  2.0435e+00, -4.5515e+00, -8.2594e-01,  2.1615e-01],
        [-1.7053e+00,  6.0295e+00,  1.5710e+00, -3.6613e+00,  4.3747e+00,
          4.9868e-01,  1.0142e-01,  5.1108e-01, -3.3526e-01, -1.5729e-01,
         -3.1905e+00, -3.6547e+00,  3.3973e-01,  4.3906e+00,  3.8088e+00]]; 
    lstm.c.biases.arr = [[ 5.3006,  1.0954,  1.2133, -4.6072,  0.6084, -1.2245,  1.9416,  1.3206,
        -0.9561,  0.4973,  1.1817, -4.1499,  2.3207, -1.9837, -0.6649]];

    lstm.y.weights.arr = [[-1.7674e+00, -9.1551e-01,  1.4949e+00,  1.4974e-01, -1.7265e-02,
         -1.2925e-02,  1.9963e-01,  3.5809e-01,  3.1470e-01,  1.2859e-01,
          2.5574e-01,  1.3834e-01,  4.3796e-01,  1.6591e-01,  1.6788e-01,
          1.5820e-01,  1.0114e-01,  8.6290e-02,  1.9773e-02,  2.2466e-01,
          1.6418e-01,  9.7070e-02, -7.7499e-02,  2.1664e-01,  8.0698e-02,
         -1.6332e-02,  2.5459e-02,  1.0120e-01,  5.2977e-02, -1.2215e-01,
          3.7580e-02, -7.6976e-01, -1.9468e+00,  6.1405e-01, -1.6245e+00,
         -2.4115e-01, -2.9883e-02,  1.4478e-01,  1.5228e+00, -2.0543e+00,
          2.4692e-01, -3.8623e-01, -1.9119e+00,  1.3723e+00, -9.4335e-01,
          3.3136e-01,  1.3025e+00,  2.4813e-01, -1.0286e+00,  9.4703e-01,
         -4.5211e-01, -1.0820e+00,  1.6659e+00,  9.0218e-01,  1.5348e+00],
        [-4.3921e+00, -4.4955e+00, -3.8535e+00,  1.5801e+00,  1.6357e+00,
          1.9940e+00,  1.8512e+00,  1.4230e+00,  1.4702e+00,  1.7169e+00,
          1.6097e+00,  1.4401e+00,  1.0925e+00,  1.4758e+00,  1.5075e+00,
          1.8046e+00,  1.5163e+00,  1.4632e+00,  1.7978e+00,  1.0470e+00,
          1.6118e+00,  1.9112e+00,  1.8557e+00,  1.1026e+00,  1.5633e+00,
          1.5577e+00,  8.8529e-01,  1.2307e+00,  1.3624e+00,  1.0164e+00,
          7.6580e-01, -3.2184e+00, -3.7584e+00,  3.5666e-03,  1.3474e+00,
         -2.0810e+00, -4.3167e+00,  1.2056e+00,  1.9383e+00, -4.1174e+00,
         -1.0698e+00, -1.1716e+00, -4.4742e+00,  2.3059e+00, -2.1177e+00,
         -5.6702e+00, -3.7787e-01, -3.3023e+00, -4.4054e+00,  1.3457e+00,
         -8.0588e-01, -1.3790e+00, -7.1800e-01,  8.9528e-01,  4.5919e+00],
        [-5.2190e+00,  2.4099e+00, -1.5045e+00,  3.1827e-01,  3.9712e-01,
          1.5779e-01,  4.3224e-01,  3.0810e-01,  2.3235e-01,  3.1193e-01,
          2.3226e-01,  1.2145e-01,  6.4806e-02,  2.9511e-01,  3.5226e-01,
          5.2242e-01,  3.0636e-01,  2.5438e-01,  4.9721e-01,  1.1039e-01,
          3.5383e-01,  5.4472e-01,  3.6850e-01,  1.0154e-01,  3.3176e-01,
          2.9373e-01,  3.3510e-01,  4.5095e-01,  1.8928e-01, -4.4057e+00,
          6.3472e-01,  2.1059e+00, -3.1348e-01, -5.0623e+00,  2.9355e+00,
         -1.4200e+00, -2.8506e+00, -5.1334e+00,  8.4819e-02,  2.4282e+00,
          4.0086e-01,  1.7901e+00,  2.8403e+00, -3.6822e+00,  9.0202e-02,
          2.0511e+00, -1.9164e-01,  5.3894e-01,  1.2324e+00, -4.0576e+00,
          2.1685e+00,  1.0018e+00,  9.6097e-01, -4.0577e+00,  5.9006e+00],
        [ 1.7331e+00,  3.6582e-01, -1.9489e-01, -2.4912e-01, -9.8876e-02,
         -2.3837e-01, -2.4056e-01, -2.3261e-01, -6.9030e-02, -2.5618e-01,
         -8.3938e-02, -1.6897e-01, -1.7138e-01, -7.9638e-02, -2.0641e-01,
         -4.3865e-02, -2.3502e-01, -3.4226e-01, -1.6478e-01, -3.7645e-01,
         -2.8034e-01, -1.0385e-01, -2.3346e-01, -3.2063e-01, -3.5670e-01,
         -3.2822e-01, -2.2001e-01,  1.4815e-02, -2.9523e-01,  7.8866e-01,
         -2.7929e-01, -6.0015e-01,  1.8726e+00,  4.5297e-02, -1.7402e-01,
          5.3947e-01, -8.9643e-01,  8.7802e-01,  1.3563e+00,  3.9863e-01,
         -2.8497e-01, -6.3427e-01, -2.8256e-01, -6.3027e-02, -2.9656e-01,
         -4.7391e-01,  2.5508e-02, -3.0475e-01,  5.7008e-01, -2.6236e-01,
          2.1726e-01,  1.9120e-01,  1.4911e-01,  7.6099e-01, -3.2305e-02],
        [-7.4885e+00,  3.6319e+00, -2.3944e+00,  1.0972e+00,  1.1201e+00,
          1.1849e+00,  9.6931e-01,  7.6678e-01,  1.0296e+00,  1.0949e+00,
          8.1427e-01,  7.2353e-01,  7.7709e-01,  7.6564e-01,  8.6287e-01,
          1.0884e+00,  7.8853e-01,  4.5458e-01,  9.6165e-01,  4.6819e-01,
          7.9460e-01,  1.1795e+00,  9.5308e-01,  7.2444e-01,  8.0420e-01,
          9.0968e-01,  5.2717e-01,  6.0409e-01,  5.9592e-01, -1.4587e+00,
          1.5276e+00, -2.5190e-01, -2.9746e-01, -2.7578e+00, -8.0399e-01,
         -4.9805e-01,  1.9421e+00, -1.9849e+00, -6.0147e+00,  4.0903e-01,
         -3.7256e-01,  3.1725e-01, -4.6336e-01, -1.6289e+00, -8.2951e-01,
          1.0270e+00,  1.1893e+00, -2.1652e-01, -3.7707e-01, -1.1124e+00,
          2.2077e+00, -5.6211e-01, -2.6114e+00, -4.1140e+00,  3.0429e+00],
        [-2.8775e+00, -3.8386e+00, -2.3951e+00, -3.7339e+00, -3.8231e+00,
         -4.0476e+00, -3.9071e+00, -3.3737e+00, -3.5828e+00, -3.9495e+00,
         -3.5478e+00, -2.7727e+00, -2.7574e+00, -3.5636e+00, -3.6746e+00,
         -4.1511e+00, -3.2963e+00, -2.8143e+00, -4.0037e+00, -2.3324e+00,
         -3.5465e+00, -4.4388e+00, -3.9103e+00, -2.5232e+00, -3.3102e+00,
         -3.4333e+00, -2.2689e+00, -2.6012e+00, -3.0077e+00,  2.4696e+00,
          5.0948e+00,  4.1078e+00,  1.6648e+00,  2.9025e+00,  4.0193e+00,
          6.2900e+00,  2.9177e+00,  2.4978e+00,  2.0026e+00,  2.3486e+00,
          3.4187e+00,  3.7192e+00,  3.8701e+00,  3.2965e+00,  3.9815e+00,
          1.9364e+00,  6.9962e+00,  3.2386e+00,  2.5935e+00,  4.5055e+00,
          6.2204e+00,  3.7139e+00,  3.1333e+00,  2.8995e+00,  6.2405e+00],
        [-6.8456e-01,  4.1380e-01,  7.7184e-01, -7.6553e-01, -7.3861e-01,
         -9.0980e-01, -1.0233e+00, -6.9716e-01, -6.3988e-01, -1.1308e+00,
         -7.8117e-01, -2.7547e-01, -5.7255e-01, -1.0061e+00, -9.8457e-01,
         -1.3103e+00, -7.0718e-01, -6.6867e-01, -1.1810e+00, -3.4294e-01,
         -1.0131e+00, -1.4645e+00, -8.4236e-01, -4.1877e-01, -7.7985e-01,
         -7.7953e-01, -2.5883e-01, -3.5115e-01, -4.6954e-01,  1.5130e+00,
          2.5774e+00,  5.0749e-01,  6.6713e-02,  6.7914e-01,  2.6965e+00,
          3.2311e-01,  8.6065e-01,  1.7013e-01, -2.2406e+00,  6.9254e-01,
          2.4343e+00,  5.8996e-01, -2.1301e-01,  6.3217e-01,  1.9117e-01,
         -1.7537e+00,  2.6363e+00,  3.0456e-01,  2.1687e+00,  1.5483e+00,
          1.0110e+00,  2.2443e+00, -2.2216e-02,  3.6353e-01, -1.9592e-01],
        [ 1.5501e+00,  5.5479e+00,  1.6430e+00,  6.5469e-01,  5.9745e-01,
          6.7445e-01,  5.0523e-01,  5.3641e-01,  3.5915e-01,  4.6612e-01,
          6.5637e-01,  3.0843e-01,  3.5504e-01,  6.9626e-01,  7.0240e-01,
          6.6207e-01,  4.7612e-01,  4.5733e-01,  6.9265e-01,  3.1572e-01,
          4.7255e-01,  6.9175e-01,  5.4129e-01,  3.5182e-01,  6.1166e-01,
          3.7490e-01,  2.2568e-01,  5.8305e-01,  5.3003e-01,  8.5983e-02,
         -1.9830e+00, -9.4846e-01, -8.3456e-01,  2.6416e-01, -2.4025e-01,
         -1.5055e+00, -6.1847e-01,  1.2785e+00, -1.3039e+00,  7.0954e-01,
         -1.5412e+00, -2.4511e+00, -6.8934e-01, -9.5162e-01, -2.3513e+00,
          8.4474e-01,  2.1724e-01, -1.8875e+00, -3.8418e-02,  2.7364e-01,
         -1.4499e+00, -8.8510e-01, -4.7352e+00,  4.1773e-01, -1.6151e+00],
        [ 5.0270e+00,  5.7861e+00,  1.5011e+00, -1.2749e+00, -1.4318e+00,
         -1.4455e+00, -1.4000e+00, -1.3326e+00, -1.4241e+00, -1.4373e+00,
         -1.2732e+00, -1.1762e+00, -1.1208e+00, -1.2591e+00, -1.3767e+00,
         -1.5247e+00, -1.0814e+00, -8.6961e-01, -1.3838e+00, -7.6226e-01,
         -1.4127e+00, -1.5423e+00, -1.2388e+00, -1.1899e+00, -1.2220e+00,
         -1.2618e+00, -6.3872e-01, -1.0280e+00, -1.2731e+00,  1.0815e+00,
          1.4149e+00,  2.6915e+00, -1.1882e-01,  8.0408e-01,  3.1868e+00,
          9.5964e-01, -9.6623e-01,  1.7114e+00,  5.0838e+00,  1.3561e+00,
         -2.1774e+00,  5.9025e-01, -9.6458e-01,  9.0474e-01,  1.7834e+00,
          1.4330e+00, -3.2156e+00,  5.3240e-01,  2.1038e+00,  1.0015e+00,
          6.7563e-01,  8.4959e-01, -1.6984e+00,  7.0961e-01,  1.2701e+00],
        [-5.6446e+00,  2.8231e+00,  6.9110e-01,  2.8602e-01,  2.9367e-01,
          2.5975e-01,  2.9706e-01,  2.1814e-01,  1.9971e-01,  2.7941e-01,
          1.0117e-01,  1.6246e-01,  1.2649e-02,  1.7358e-01,  3.0113e-01,
          2.0530e-01,  1.2492e-01,  2.1184e-01,  1.7728e-01,  3.6267e-02,
          1.5123e-01,  3.5152e-01,  2.1237e-01, -1.1228e-02,  1.0536e-01,
          1.8062e-01, -2.8624e-02,  1.3219e-01,  6.1520e-02,  4.0948e-01,
          5.4538e-01,  3.2287e-01, -8.1914e-01,  9.4937e-01,  5.2537e-01,
          1.0695e+00, -1.9646e+00,  6.7824e-01, -1.0425e+00, -3.9489e-01,
         -2.2242e-01,  2.5357e-01, -3.0078e-01, -1.0191e+00, -1.2341e-01,
         -2.9353e+00,  6.4397e-01, -1.7737e-01, -3.4137e-01, -3.9261e-01,
         -9.2652e-01,  9.5617e-01,  1.7195e+00,  1.4561e+00, -8.7697e-01],
        [-1.9822e-02, -4.1414e+00,  5.6925e-01,  1.7210e+00,  1.6861e+00,
          1.9239e+00,  1.7448e+00,  1.4888e+00,  1.6471e+00,  1.8772e+00,
          1.6667e+00,  1.2369e+00,  1.2073e+00,  1.6754e+00,  1.7375e+00,
          1.8477e+00,  1.4866e+00,  1.3859e+00,  1.7573e+00,  1.0823e+00,
          1.6567e+00,  2.0662e+00,  1.7413e+00,  1.2649e+00,  1.5222e+00,
          1.5606e+00,  8.5004e-01,  1.2188e+00,  1.4745e+00, -5.7182e+00,
          5.7236e-01,  1.4838e+00, -4.3187e-01, -4.6493e+00, -4.3707e-01,
          1.3830e+00, -3.7481e+00, -4.0465e+00, -1.3084e+00,  1.9459e-01,
         -3.1252e+00, -1.8663e-01,  5.1788e-02, -5.1506e+00,  1.4190e+00,
          3.7905e-01, -3.1350e+00,  2.1634e-01, -1.8659e-01, -3.4196e+00,
         -3.5782e-01, -2.9854e+00,  1.7058e+00, -3.8568e+00, -9.0380e-01],
        [ 7.1933e-01, -1.1197e+00, -1.6313e+00, -2.6536e-01, -4.0675e-01,
         -3.2037e-01, -2.6350e-01, -4.2763e-01, -3.9077e-01, -2.5584e-01,
         -3.0190e-01, -3.5204e-01, -9.7195e-02, -2.3084e-01, -3.8444e-01,
         -2.5849e-01, -1.6119e-01, -1.9991e-01, -2.8845e-01, -1.5117e-01,
         -3.0226e-01, -3.6811e-01, -1.9598e-01, -2.8321e-01, -2.5888e-01,
         -1.6250e-01, -3.4527e-01, -3.6154e-01, -3.3149e-01, -8.1154e-01,
          5.0198e-01,  1.6323e-01,  7.6622e-01, -3.7444e-01, -9.0902e-01,
          1.2349e+00,  5.0417e-01,  2.1939e-01, -3.8206e+00, -1.3533e+00,
          1.7700e+00,  9.7895e-01,  1.8938e+00,  6.5565e-02,  2.1640e+00,
          1.8184e-01,  1.9697e+00,  7.1587e-01,  4.4474e-01,  2.3438e+00,
          1.3230e+00,  2.3344e+00,  1.8960e+00,  1.4289e+00, -3.5977e+00],
        [-2.9154e+00, -1.3173e+00,  5.4739e-01,  1.9928e+00,  2.0744e+00,
          2.1247e+00,  2.0271e+00,  1.9569e+00,  1.9233e+00,  2.0546e+00,
          2.0394e+00,  1.4315e+00,  1.4537e+00,  1.7559e+00,  1.9816e+00,
          2.0652e+00,  1.8362e+00,  1.5632e+00,  2.0790e+00,  1.3708e+00,
          1.9119e+00,  2.2584e+00,  2.1438e+00,  1.2774e+00,  1.7652e+00,
          1.7296e+00,  1.3725e+00,  1.2264e+00,  1.6377e+00, -1.5591e+00,
         -2.8252e+00, -3.0684e-01, -4.3979e+00, -2.0047e+00, -3.6568e+00,
         -4.1140e+00,  1.1715e+00, -2.2111e+00, -8.6737e-01, -6.7442e-01,
         -2.8851e+00, -2.1636e+00, -1.8954e+00, -8.1372e-01,  7.7889e-01,
         -3.2996e-01, -3.3706e+00, -1.3558e+00,  1.6420e-01, -5.1055e-01,
         -2.7560e+00, -9.8084e-01, -4.3690e-01, -1.4564e+00, -6.3340e+00],
        [-1.1911e+00, -9.0373e+00, -1.0665e-01, -3.8091e-01, -6.3222e-01,
         -6.5566e-01, -5.1739e-01, -4.2233e-01, -2.9868e-01, -3.1425e-01,
         -4.9221e-01, -5.7947e-01, -5.3550e-01, -3.0622e-01, -4.2568e-01,
         -3.7591e-01, -3.2994e-01, -3.9113e-01, -4.3038e-01, -4.0531e-01,
         -4.9615e-01, -4.7522e-01, -5.2924e-01, -4.4287e-01, -5.0951e-01,
         -3.6197e-01, -4.6018e-01, -4.7616e-01, -3.4511e-01,  3.4942e-01,
          1.6379e+00,  1.2312e+00,  3.8985e-01,  1.4703e+00,  3.0137e+00,
         -2.0529e+00,  1.5682e+00,  1.1621e+00,  1.5760e+00,  5.5700e-01,
          2.9646e+00,  9.6476e-01, -3.5167e-01,  2.6394e-02,  7.8809e-01,
          7.1518e-01, -4.9494e-01, -6.6684e-01,  1.2240e+00, -6.5940e-02,
          2.5374e+00,  2.8766e+00, -1.9896e+00,  5.1527e-01,  2.6546e+00],
        [ 5.0455e+00, -3.4152e+00, -3.6369e-01, -1.0779e-01, -9.8698e-02,
         -5.6390e-02, -3.3114e-01, -4.0828e-01, -9.2182e-02, -1.1283e-01,
         -3.0703e-01, -1.8835e-01, -3.7625e-01, -4.1074e-01, -2.7048e-01,
         -2.7882e-01, -1.5322e-01, -1.6989e-01, -3.3087e-01, -3.6062e-01,
         -1.5117e-01, -1.9595e-01, -3.3419e-01, -2.6092e-01, -2.0889e-01,
         -1.0171e-01, -1.7833e-01, -3.6373e-01, -3.8153e-01,  9.7952e-01,
         -5.6473e-02, -1.3290e+00,  1.1610e+00,  5.9781e-01, -7.8789e-01,
         -2.0501e+00,  2.6490e+00,  6.6870e-01,  7.6085e-01,  1.1390e+00,
         -4.2311e-01,  1.0880e+00, -5.2198e-01,  3.9321e-01,  2.0759e-01,
         -1.3007e+00,  4.0326e-01,  1.4655e+00, -1.7665e-01, -1.0777e+00,
         -1.7154e+00,  3.6579e-01, -3.7966e-01, -9.4430e-02,  2.8149e+00]];
    lstm.y.biases.arr = [[-0.6527, -4.5182, -2.8644, -1.6091, -1.5705, -1.4875, -1.5673, -1.7915,
        -1.6305, -1.4857, -1.6602, -1.8999, -1.8788, -1.5876, -1.6249, -1.4144,
        -1.6515, -1.8272, -1.4896, -2.1018, -1.6130, -1.3767, -1.5463, -2.0001,
        -1.6943, -1.6012, -2.1365, -2.0231, -1.8536,  5.4001,  0.7030,  1.3164,
         3.3273,  5.2878, -0.7130, -0.4701,  2.3502,  5.6425, -2.3973,  2.2060,
         3.9828,  2.4913,  2.5228,  4.8637,  2.8241, -0.8152,  1.9531,  2.8744,
         3.3195,  3.6200, -1.0678,  2.4399, -0.1796,  4.2195, -4.5278]];
}

</script>

<style scoped>

#topic {
    text-align: center;
    margin: 80px 0px;
}

#names {
    font-family: monospace;
}

</style>